import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMasterData } from '../../hooks/useMasterData';
import { useDateContext } from '../../hooks/useDate';
import { useAuth } from '../../hooks/useAuth';
import { useAuthorization } from '../../hooks/useAuthorization';
import { useGlobalKeyboard, KEYBOARD_PRIORITIES } from '../../hooks/useGlobalKeyboard';
import {
  useVehicleModelSelection,
  VehicleModelSelectionState,
} from '../../hooks/useVehicleModelSelection';
import {
  Case,
  SuggestedTransporterInfo,
  Role,
  DeadlineRuleType,
  DeadlineCheckResult,
  DeadlineRule,
  DayOfWeek,
} from '../../types';
import {
  getDeadlineRulesByBusinessPartnerId as getDeadlineRulesByBP,
  checkDeadlineViolation as checkDeadline,
} from '../../services/deadlineRuleService';
import { getSuggestedLandTransporters } from '../../services/geminiService';
import { distanceCalculationService } from '../../services/maps/distanceCalculationService';
import {
  DocumentPlusIcon,
  ArrowLeftIcon,
  TruckIcon,
  UserIcon,
  SparklesIcon,
  XMarkIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { Button, PageTitle, Alert } from '../../components/ui';
import { BusinessPartnerForm } from '../../components/forms/BusinessPartnerForm';
import { BusinessPartnerSelector } from '../../components/forms/BusinessPartnerSelector';
import { VehicleModelSelector } from '../../components/forms/VehicleModelSelector';
import { VehiclePlateNumberValidator } from '../../components/forms/VehiclePlateNumberValidator';
import { ImageUpload } from '../../components/forms/ImageUpload';
import { AddressSelectionUI } from '../../components/forms/AddressSelectionUI';
import { TimeSelector } from '../../components/ui/TimeSelector';
import CaseStatusManager from '../../components/cases/CaseStatusManager';
import { AddressAutocompleteInput } from '../../components/forms/AddressAutocompleteInput';
import { RouteVisualizationComponent } from '../../components/maps/RouteVisualizationComponent';
// import { ComboAddressInput } from '../../components/forms/ComboAddressInput';
// import { VehicleSearchSelector } from '../../components/forms/VehicleSearchSelector';
// import { DateRangeSelector } from '../../components/forms/DateRangeSelector';
import {
  DEFAULT_PICKUP_TIME_SETTINGS,
  DEFAULT_DELIVERY_TIME_SETTINGS,
} from '../../utils/timePickerDefaults';
import { validateCaseFormTimes } from '../../utils/timeValidation';
import { formatTimeToHHMM } from '../../utils/timeFormatter';
import {
  ExtractedAddress,
  OCREventHandlers,
  ImageOCRResult,
  DEFAULT_IMAGE_UPLOAD_CONFIG,
  BusinessPartnerRole,
} from '../../types';
import type { RouteVisualizationData } from '../../types/maps';
import { DEFAULT_TRANSPORT_FEE } from '../../constants/company';
import { getCompanyId } from '../../utils/company';
import { detectMultipleAddressTypes } from '../../utils/addressAutoDetection';
import { extractPhoneNumbersFromMultipleTexts } from '../../utils/addressExtraction';
import { debugLog } from '../../utils/environment';
import logger from '../../utils/logger';
import { validateVehicleData } from '../../utils/vehicleUtils';

/**
 * FL-163: 受付ステータス管理フィールドのデフォルト値
 * Note: acceptanceStatus removed - now using Case.status directly
 */
const DEFAULT_FL163_FIELDS = {
  chassisNumber: '',
} as const;

export const CaseFormPage: React.FC = () => {
  debugLog('[CaseFormPage] Component mounted (initialization started)');

  // アンマウント対策: マウント状態を追跡
  const isMounted = useRef(true);

  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId?: string }>();
  const [searchParams] = useSearchParams();
  const { selectedDate, setSelectedDate } = useDateContext();
  const auth = useAuth();
  const { checkPermission } = useAuthorization();
  // FL-208: 陸送員は料金情報を表示・編集しない
  const canViewPricing = checkPermission([
    Role.SUPER_ADMIN,
    Role.ADMIN,
    Role.DISPATCHER,
    Role.READ_ONLY,
  ]);

  // FL-163: スケジュール画面からのクエリパラメータ取得
  const queryTransporterId = searchParams.get('transporterId');
  const queryPickupDate = searchParams.get('pickupDate');
  const queryPickupTimeStart = searchParams.get('pickupTimeStart');

  // 統一キーボード管理フック
  const { registerHandler } = useGlobalKeyboard();

  debugLog('[CaseFormPage] 基本フック初期化完了', { caseId, selectedDate, user: auth.user });
  const {
    clients,
    landTransporters,
    getBusinessPartnerById,
    getBusinessPartnerLocationById,
    getCompanyOCRSettingsByCompanyId,
    addCase,
    updateCase,
    getCaseById,
    getCompanyTimeSettingsByCompanyId,
    businessPartners,
    cases,
    loading,
  } = useMasterData();

  debugLog('[CaseFormPage] MasterDataContext初期化完了');

  const isEditMode = Boolean(caseId);
  const [isLoading, setIsLoading] = useState(false);

  // ユーザーが時間選択で何らかの操作を行ったかを管理するフラグ
  const [hasInteracted, setHasInteracted] = useState(false);

  // OCR設定を管理（会社IDはユーザー情報から取得）
  const companyId = getCompanyId(auth.user);
  logger.info('[CaseFormPage] 使用するcompanyId:', companyId);
  logger.info('[CaseFormPage] auth.user:', auth.user);

  // 陸送費設定を状態として管理
  const [transportFeeSettings, setTransportFeeSettings] = useState({
    calculation_method: 'fixed' as const,
    fixed_fee: DEFAULT_TRANSPORT_FEE,
  });

  // 締切違反チェック結果を管理
  const [deadlineWarnings, setDeadlineWarnings] = useState<{
    pickup?: DeadlineCheckResult;
    delivery?: DeadlineCheckResult;
  }>({});
  // 締切ルール機能は関数ファサード経由で呼び出す（テスト容易性のため）

  // 締切ルール適用状態を管理
  interface AppliedDeadlineRule {
    rule: DeadlineRule;
    dayOfWeek: DayOfWeek;
    partnerName: string;
  }

  const [appliedDeadlineRules, setAppliedDeadlineRules] = useState<{
    pickup?: AppliedDeadlineRule;
    delivery?: AppliedDeadlineRule;
  }>({});

  // 手動変更フラグ管理
  const [manuallyOverridden, setManuallyOverridden] = useState({
    pickupTimeStart: false,
    pickupTimeEnd: false,
    deliveryTime: false,
  });

  // 締切ルール処理中のローディング状態管理
  const [deadlineRuleLoading, setDeadlineRuleLoading] = useState({
    pickup: false,
    delivery: false,
  });

  // 手動変更フラグの確認用（デバッグ）
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const hasManualChanges = Object.values(manuallyOverridden).some(Boolean);
      if (hasManualChanges) {
        logger.debug('[DeadlineRule] 手動変更フラグ状態:', manuallyOverridden);
      }
    }
  }, [manuallyOverridden]);

  // 陸送費設定をSupabaseから取得（初期化時）
  useEffect(() => {
    const fetchTransportFeeSettings = async () => {
      try {
        logger.info('[CaseFormPage] 陸送費設定取得開始', { companyId });
        const { getSupabaseClient } = await import('../../utils/supabase');
        const supabase = getSupabaseClient();
        logger.info('[CaseFormPage] Supabaseクライアント:', supabase ? '初期化済み' : 'null');
        if (!supabase) {
          logger.warn('[CaseFormPage] Supabaseクライアントが利用できません');
          return;
        }

        const { data, error } = await supabase
          .from('companies')
          .select('settings')
          .eq('id', companyId)
          .single();

        if (error) {
          logger.error('[CaseFormPage] 陸送費設定取得エラー:', error);
          logger.error('[CaseFormPage] エラー詳細:', {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint,
          });
          return;
        }

        logger.info('[CaseFormPage] Supabaseレスポンス:', { data, error });
        logger.info('[CaseFormPage] settings:', data?.settings);
        logger.info(
          '[CaseFormPage] transport_fee_settings:',
          data?.settings?.transport_fee_settings
        );

        if (data?.settings?.transport_fee_settings) {
          logger.info('[CaseFormPage] 陸送費設定取得成功:', data.settings.transport_fee_settings);
          setTransportFeeSettings(data.settings.transport_fee_settings);
        } else {
          logger.info(
            '[CaseFormPage] 陸送費設定がない場合、デフォルト値を使用:',
            DEFAULT_TRANSPORT_FEE
          );
        }
      } catch (error) {
        logger.error('[CaseFormPage] 陸送費設定取得エラー:', error);
        logger.error('[CaseFormPage] エラータイプ:', typeof error);
        logger.error('[CaseFormPage] エラー内容:', error);
        if (error instanceof Error) {
          logger.error('[CaseFormPage] エラーメッセージ:', error.message);
          logger.error('[CaseFormPage] エラースタック:', error.stack);
        }
      }
    };

    fetchTransportFeeSettings();
  }, [companyId]);

  // 陸送費設定が変更されたときにformDataを更新
  useEffect(() => {
    logger.info('[CaseFormPage] transportFeeSettings変更:', transportFeeSettings);
    logger.info(
      '[CaseFormPage] formDataのtransportFeeを更新:',
      transportFeeSettings.fixed_fee || DEFAULT_TRANSPORT_FEE
    );
    setFormData(prev => ({
      ...prev,
      transportFee: transportFeeSettings.fixed_fee || DEFAULT_TRANSPORT_FEE,
    }));
  }, [transportFeeSettings]);

  // FL-163: スケジュール画面からのクエリパラメータを適用
  useEffect(() => {
    if (!caseId && queryTransporterId && queryPickupDate && queryPickupTimeStart) {
      logger.info('🔵 クエリパラメータ適用:', {
        queryTransporterId,
        queryPickupDate,
        queryPickupTimeStart,
      });
      setFormData(prev => ({
        ...prev,
        assignedLandTransporterId: queryTransporterId,
        pickupDate: queryPickupDate,
        pickupTimeStart: queryPickupTimeStart,
        deliveryDate: queryPickupDate, // デフォルトは同日
      }));
      // 日付コンテキストも更新
      setSelectedDate(queryPickupDate);
    }
  }, [caseId, queryTransporterId, queryPickupDate, queryPickupTimeStart, setSelectedDate]);

  // OCR設定をuseMemoで取得（レンダー最適化）
  const ocrSettings = useMemo(() => {
    try {
      logger.info('[CaseFormPage] OCR設定取得開始', { companyId });
      const settings = getCompanyOCRSettingsByCompanyId(companyId);
      logger.info('[CaseFormPage] OCR設定取得成功', settings);
      return settings;
    } catch (error) {
      logger.error('[CaseFormPage] OCR設定取得エラー:', error);
      // フォールバック: デフォルト設定を返す
      const fallbackSettings = {
        id: `ocr-settings-fallback`,
        companyId,
        ocrEnabled: true,
        autoAddressExtraction: true,
        autoPhoneExtraction: true,
        confidenceThreshold: 0.3,
        maxFilesPerUpload: 3,
        maxFileSizeMB: 10,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      logger.info('[CaseFormPage] フォールバック設定を使用', fallbackSettings);
      return fallbackSettings;
    }
  }, [companyId, getCompanyOCRSettingsByCompanyId]);

  // FL-59: 時間バリデーション状態
  const [timeValidationErrors, setTimeValidationErrors] = useState<Record<string, string>>({});

  // 権限チェック
  React.useEffect(() => {
    try {
      checkPermission([Role.ADMIN, Role.DISPATCHER]);
    } catch (error) {
      logger.error('[CaseFormPage] 権限チェックエラー:', error);
      navigate('/dashboard');
    }
  }, [checkPermission, navigate]);

  // ステータス更新時のハンドラ
  const handleStatusUpdated = useCallback((newStatus: Case['status']) => {
    setFormData(prevData => ({
      ...prevData,
      status: newStatus,
    }));
    logger.info(`✅ 案件ステータスを ${newStatus} に更新`);
  }, []);

  // ステータス更新エラーハンドラ
  const handleStatusUpdateError = useCallback((error: Error) => {
    logger.error('❌ ステータス更新エラー:', error);
  }, []);

  // フォームの初期状態（selectedDateを使用）
  type FormData = Omit<Case, 'id' | 'pickupReport' | 'deliveryReport'> & {
    // フォーム専用フィールド
    title?: string;
    description?: string;
    category?: 'immediate' | 'scheduled';
    priority?: 'High' | 'Medium' | 'Low';
    clientId?: string;
    // FL-89: 期間選択モード用フラグ
    pickupDateRangeMode?: boolean;
    deliveryDateRangeMode?: boolean;
    // 自由入力用フィールド
    customBillingPartnerName?: string;
    customPickupPartnerName?: string;
    customDeliveryPartnerName?: string;
    // FL-35: Google Maps API 統合フィールド
    distance_km?: number;
    estimated_duration_minutes?: number;
    distance_calculated_at?: string;
  };

  const [formData, setFormData] = useState<FormData>({
    // Case型の必須フィールド
    status: 'provisional', // FL-142: 新規案件は仮受から開始
    billingPartnerId: '',
    pickupPartnerId: '',
    deliveryPartnerId: '',
    billingClientId: '',
    pickupClientId: '',
    deliveryClientId: '',
    pickupAddress: '',
    pickupPhone: '',
    pickupDate: selectedDate,
    pickupTimeStart: '',
    pickupTimeEnd: '',
    deliveryAddress: '',
    deliveryPhone: '',
    deliveryDate: selectedDate,
    deliveryTime: '',
    vehicleModel: '',
    vehicleType: '',
    vehiclePlateNumber: '',
    price: 0,
    remarks: '',
    // オプションフィールド
    assignedLandTransporterId: '',
    // FL-89: 新規フィールド
    requester: '',
    managementNumber: '',
    transportFee: transportFeeSettings.fixed_fee || DEFAULT_TRANSPORT_FEE,
    billingAddressCustom: '',
    pickupAddressCustom: '',
    deliveryAddressCustom: '',
    pickupDateEnd: '',
    deliveryDateEnd: '',
    vehiclePlateNumberNone: false,
    // FL-113: 車検状態管理フィールド
    isVehicleInspectionExpired: false,
    requiresTemporaryNumber: false,
    temporaryNumberNotes: '',
    // FL-205: マッチング条件フィールド
    transmission_type: 'automatic', // デフォルト: AT
    steering_position: 'right', // デフォルト: 右ハンドル
    has_vehicle_inspection: true, // デフォルト: 車検あり
    // FL-163: 受付ステータス管理フィールド
    ...DEFAULT_FL163_FIELDS,
    // FL-35/FL-170: Google Maps API 統合フィールド
    distance_km: undefined,
    estimated_duration_minutes: undefined,
    distance_calculated_at: undefined,
    // フォーム専用フィールド
    title: '',
    description: '',
    category: 'immediate',
    priority: 'Medium',
    clientId: '',
    pickupDateRangeMode: false,
    deliveryDateRangeMode: false,
  } as FormData);

  // FL-28: BusinessPartner選択用のstate
  const [businessPartnerSelections, setBusinessPartnerSelections] = useState<{
    billing?: { partnerId: string; locationId?: string };
    pickup?: { partnerId: string; locationId?: string };
    delivery?: { partnerId: string; locationId?: string };
  }>({});

  // FL-170: ルート可視化データ（Google Maps Directions API統合）
  const [routeVisualizationData, setRouteVisualizationData] =
    useState<RouteVisualizationData | null>(null);

  // バッファ不足状態の追跡（引取）
  const [pickupEndWasInsufficient, setPickupEndWasInsufficient] = useState(false);
  const pickupEndRef = useRef<string>('');
  useEffect(() => {
    pickupEndRef.current = formData.pickupTimeEnd || '';
  }, [formData.pickupTimeEnd]);

  // 最新の取引先選択状態を追跡（非同期処理競合防止）
  const latestPartnerSelection = useRef<{
    pickup?: string | null;
    delivery?: string | null;
  }>({
    pickup: null,
    delivery: null,
  });

  // 締切ルールキャッシュ（同一取引先の再選択時に高速表示）
  const deadlineRulesCache = useRef<Record<string, DeadlineRule[]>>({});

  // FL-59: 時間設定取得
  const currentCompanyId = 'default-company'; // TODO: PR #109 対応 - 会社IDハードコード修正
  // 実際の会社IDを取得する仕組みを実装（AuthContextから取得、または環境変数から取得）
  const companyTimeSettings = useMemo(
    () => getCompanyTimeSettingsByCompanyId(currentCompanyId),
    [currentCompanyId, getCompanyTimeSettingsByCompanyId]
  );

  // アンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // FL-59: 引き取り時間変更時の搬入時間自動更新
  useEffect(() => {
    if (formData.pickupTimeStart) {
      const [pickupHour, pickupMinute] = formData.pickupTimeStart.split(':').map(Number);
      const pickupMinutes = pickupHour * 60 + pickupMinute;
      const minimumDuration =
        companyTimeSettings?.deliveryTimeSettings?.minimumDuration ??
        DEFAULT_DELIVERY_TIME_SETTINGS.minimumDuration;

      const minDeliveryMinutes = pickupMinutes + minimumDuration;

      const deliveryHour = Math.floor(minDeliveryMinutes / 60);
      const deliveryMinute = minDeliveryMinutes % 60;
      const minDeliveryTime = `${deliveryHour.toString().padStart(2, '0')}:${deliveryMinute
        .toString()
        .padStart(2, '0')}`;

      // 現在の搬入時間が最小必要時間より前の場合、自動更新
      if (!formData.deliveryTime || formData.deliveryTime < minDeliveryTime) {
        setFormData(prev => ({
          ...prev,
          deliveryTime: minDeliveryTime,
        }));
      }
    }
  }, [formData.pickupTimeStart, companyTimeSettings]);

  // FL-59: 時間変更時のバリデーション
  // 引取終了時間の動的制限設定
  const pickupEndTimeSettings = useMemo(() => {
    const baseSettings = companyTimeSettings?.pickupTimeSettings || DEFAULT_PICKUP_TIME_SETTINGS;

    if (formData.pickupTimeStart) {
      // バッファ時間を取得（デフォルト30分）
      const bufferMinutes = companyTimeSettings?.bufferTimeSettings?.betweenCasesBuffer ?? 30;

      // 引取開始時間を分に変換
      const [startHour, startMinute] = formData.pickupTimeStart.split(':').map(Number);
      const startTotalMinutes = startHour * 60 + startMinute;

      // バッファ時間を加算
      const minEndTotalMinutes = startTotalMinutes + bufferMinutes;
      const minEndHour = Math.floor(minEndTotalMinutes / 60) % 24;
      const minEndMinute = minEndTotalMinutes % 60;

      // HH:mm形式に変換
      const minTime = `${minEndHour.toString().padStart(2, '0')}:${minEndMinute.toString().padStart(2, '0')}`;

      return {
        ...baseSettings,
        minTime, // 引取開始時間＋バッファ時間を最小値に設定
      };
    }

    return baseSettings;
  }, [
    formData.pickupTimeStart,
    companyTimeSettings?.pickupTimeSettings,
    companyTimeSettings?.bufferTimeSettings?.betweenCasesBuffer,
  ]);

  useEffect(() => {
    // hasInteractedがfalse（初期表示）の場合はバリデーションをスキップ
    if (!hasInteracted) {
      setTimeValidationErrors({});
      return;
    }

    {
      const pickupSettings =
        companyTimeSettings?.pickupTimeSettings || DEFAULT_PICKUP_TIME_SETTINGS;
      const deliverySettings =
        companyTimeSettings?.deliveryTimeSettings || DEFAULT_DELIVERY_TIME_SETTINGS;

      const validationResults = validateCaseFormTimes(
        {
          pickupTimeStart: formData.pickupTimeStart,
          pickupTimeEnd: formData.pickupTimeEnd,
          deliveryTime: formData.deliveryTime,
        },
        pickupSettings,
        deliverySettings
      );

      // エラーメッセージのマップを作成
      const errorMap: Record<string, string> = {};
      Object.entries(validationResults).forEach(([field, result]) => {
        if (!result.isValid && result.errorMessage) {
          errorMap[field] = result.errorMessage;
        }
      });

      setTimeValidationErrors(errorMap);
    }
  }, [
    formData.pickupTimeStart,
    formData.pickupTimeEnd,
    formData.deliveryTime,
    hasInteracted,
    companyTimeSettings,
  ]);

  // AI提案関連のstate
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  const [suggestedTransporters, setSuggestedTransporters] = useState<SuggestedTransporterInfo[]>(
    []
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string>('');

  // データ同期強制用のstate
  const [, setForceUpdateKey] = useState(0);

  // 車両モデル選択変更コールバック
  const vehicleModelChangeCallback = useCallback((state: VehicleModelSelectionState) => {
    // 車両モデル選択状態が変更された時にformDataを更新
    const updateData: Partial<FormData> = {
      vehicleModelId: state.vehicleModel, // state.vehicleModelはUUIDを含む
      vehicleType: state.vehicleType,
    };

    // ナンバープレート番号の自動生成が有効な場合、ナンバープレート番号も更新
    if (state.shouldAutoGeneratePlateNumber && state.vehiclePlateNumber) {
      updateData.vehiclePlateNumber = state.vehiclePlateNumber;
      logger.info('[CaseFormPage] ナンバープレート番号を自動生成:', state.vehiclePlateNumber);
    }

    setFormData(prev => ({
      ...prev,
      ...updateData,
    }));
  }, []);

  // 車両モデル選択管理
  const { handleVehicleModelChange } = useVehicleModelSelection(
    formData.vehicleModelId || '',
    formData.vehicleType,
    vehicleModelChangeCallback
  );

  // 車両詳細情報のキャッシュ

  // FL-67: 引取先住所用OCR状態管理
  const [, setPickupAddressOCRResults] = useState<ImageOCRResult[]>([]);
  const [pickupAddressAddresses, setPickupAddressAddresses] = useState<ExtractedAddress[]>([]);
  const [showPickupAddressSelection, setShowPickupAddressSelection] = useState(false);

  // FL-67: 搬入先住所用OCR状態管理
  const [, setDeliveryAddressOCRResults] = useState<ImageOCRResult[]>([]);
  const [deliveryAddressAddresses, setDeliveryAddressAddresses] = useState<ExtractedAddress[]>([]);
  const [showDeliveryAddressSelection, setShowDeliveryAddressSelection] = useState(false);

  // 編集モードの場合、既存データを読み込み
  useEffect(() => {
    // loading中はスキップ（MasterDataContextのデータ読み込み完了を待つ）
    if (loading) {
      return;
    }

    if (isEditMode && caseId) {
      logger.info('[CaseFormPage] 編集モード - データ初期化開始:', caseId);

      const existingCase = getCaseById(caseId);

      if (existingCase) {
        logger.info('[CaseFormPage] MasterDataContext状態確認:', {
          businessPartnersCount: businessPartners.length,
          casesCount: cases.length,
          existingCasePartnerIds: {
            billing: existingCase.billingPartnerId,
            pickup: existingCase.pickupPartnerId,
            delivery: existingCase.deliveryPartnerId,
          },
        });
        logger.info('[CaseFormPage] 案件データ取得成功:', {
          id: existingCase.id,
          assignedLandTransporterId: existingCase.assignedLandTransporterId,
          status: existingCase.status,
          pickupAddress: existingCase.pickupAddress,
          vehicleModel: existingCase.vehicleModel,
          vehicleType: existingCase.vehicleType,
          vehiclePlateNumber: existingCase.vehiclePlateNumber,
          deliveryAddress: existingCase.deliveryAddress,
          pickupTimeStart: existingCase.pickupTimeStart,
          pickupTimeEnd: existingCase.pickupTimeEnd,
          deliveryTime: existingCase.deliveryTime,
        });

        const updatedData = {
          ...existingCase,
          pickupDate: existingCase.pickupDate
            ? new Date(existingCase.pickupDate).toISOString().split('T')[0]
            : '',
          deliveryDate: existingCase.deliveryDate
            ? new Date(existingCase.deliveryDate).toISOString().split('T')[0]
            : '',
          // 時間フィールドを HH:mm 形式に変換（HH:mm:ss → HH:mm）
          pickupTimeStart: formatTimeToHHMM(existingCase.pickupTimeStart),
          pickupTimeEnd: formatTimeToHHMM(existingCase.pickupTimeEnd),
          deliveryTime: formatTimeToHHMM(existingCase.deliveryTime),
        };

        logger.info('[CaseFormPage] FormData設定前の住所・時間・車両確認:', {
          pickupAddress: updatedData.pickupAddress,
          deliveryAddress: updatedData.deliveryAddress,
          pickupPartnerId: updatedData.pickupPartnerId,
          deliveryPartnerId: updatedData.deliveryPartnerId,
          pickupTimeStart: updatedData.pickupTimeStart,
          pickupTimeEnd: updatedData.pickupTimeEnd,
          deliveryTime: updatedData.deliveryTime,
          vehicleModel: updatedData.vehicleModel,
          vehicleType: updatedData.vehicleType,
        });

        // FL-132: 編集モード時に住所が空の場合、取引先から自動設定
        if (updatedData.pickupPartnerId && !updatedData.pickupAddress) {
          const pickupPartner = getBusinessPartnerById(updatedData.pickupPartnerId);
          if (pickupPartner) {
            const defaultLocation = pickupPartner.locations?.find(loc => loc.isDefault);
            if (defaultLocation) {
              updatedData.pickupAddress = defaultLocation.address;
              updatedData.pickupPhone = defaultLocation.phone;
              logger.info('[CaseFormPage] 引取先住所を自動設定:', defaultLocation.address);
            } else if (pickupPartner.defaultAddress) {
              updatedData.pickupAddress = pickupPartner.defaultAddress;
              updatedData.pickupPhone = pickupPartner.defaultPhone || '';
              logger.info(
                '[CaseFormPage] 引取先住所をデフォルトから設定:',
                pickupPartner.defaultAddress
              );
            }
          }
        }

        if (updatedData.deliveryPartnerId && !updatedData.deliveryAddress) {
          const deliveryPartner = getBusinessPartnerById(updatedData.deliveryPartnerId);
          if (deliveryPartner) {
            const defaultLocation = deliveryPartner.locations?.find(loc => loc.isDefault);
            if (defaultLocation) {
              updatedData.deliveryAddress = defaultLocation.address;
              updatedData.deliveryPhone = defaultLocation.phone;
              logger.info('[CaseFormPage] 搬入先住所を自動設定:', defaultLocation.address);
            } else if (deliveryPartner.defaultAddress) {
              updatedData.deliveryAddress = deliveryPartner.defaultAddress;
              updatedData.deliveryPhone = deliveryPartner.defaultPhone || '';
              logger.info(
                '[CaseFormPage] 搬入先住所をデフォルトから設定:',
                deliveryPartner.defaultAddress
              );
            }
          }
        }

        setFormData(updatedData);

        // デバッグ: setFormData後の実際の値を確認
        logger.info('[CaseFormPage] setFormData実行後の時間フィールド確認:', {
          pickupTimeStart: updatedData.pickupTimeStart,
          pickupTimeEnd: updatedData.pickupTimeEnd,
          deliveryTime: updatedData.deliveryTime,
        });

        // FL-28 & FL-132: BusinessPartner選択状態も復元（デバッグログ強化）
        // 取引先データが実際に存在するかを検証
        const billingPartner = existingCase.billingPartnerId
          ? getBusinessPartnerById(existingCase.billingPartnerId)
          : null;
        const pickupPartner = existingCase.pickupPartnerId
          ? getBusinessPartnerById(existingCase.pickupPartnerId)
          : null;
        const deliveryPartner = existingCase.deliveryPartnerId
          ? getBusinessPartnerById(existingCase.deliveryPartnerId)
          : null;

        logger.info('[CaseFormPage] 取引先データ存在確認:', {
          billing: {
            id: existingCase.billingPartnerId,
            exists: !!billingPartner,
            name: billingPartner?.name,
          },
          pickup: {
            id: existingCase.pickupPartnerId,
            exists: !!pickupPartner,
            name: pickupPartner?.name,
          },
          delivery: {
            id: existingCase.deliveryPartnerId,
            exists: !!deliveryPartner,
            name: deliveryPartner?.name,
          },
        });

        const businessPartnerRestorationData = {
          billing: existingCase.billingPartnerId
            ? {
                partnerId: existingCase.billingPartnerId,
                locationId: existingCase.billingLocationId,
              }
            : undefined,
          pickup: existingCase.pickupPartnerId
            ? {
                partnerId: existingCase.pickupPartnerId,
                locationId: existingCase.pickupLocationId,
              }
            : undefined,
          delivery: existingCase.deliveryPartnerId
            ? {
                partnerId: existingCase.deliveryPartnerId,
                locationId: existingCase.deliveryLocationId,
              }
            : undefined,
        };

        logger.info('[CaseFormPage] 編集モード - BusinessPartner選択状態復元:', {
          existingCase: {
            billingPartnerId: existingCase.billingPartnerId,
            billingLocationId: existingCase.billingLocationId,
            pickupPartnerId: existingCase.pickupPartnerId,
            pickupLocationId: existingCase.pickupLocationId,
            deliveryPartnerId: existingCase.deliveryPartnerId,
            deliveryLocationId: existingCase.deliveryLocationId,
          },
          restored: businessPartnerRestorationData,
          formData: {
            billingPartnerId: updatedData.billingPartnerId,
            pickupPartnerId: updatedData.pickupPartnerId,
            deliveryPartnerId: updatedData.deliveryPartnerId,
          },
        });

        // ✅ デバッグ: 搬入先が正しく設定されているか確認
        logger.info('🔍 [CaseFormPage] 搬入先デバッグ - 詳細確認:', {
          caseId: existingCase.id,
          deliveryPartnerId: existingCase.deliveryPartnerId,
          deliveryPartnerExists: !!deliveryPartner,
          deliveryPartnerName: deliveryPartner?.name,
          formDataDeliveryPartnerId: updatedData.deliveryPartnerId,
          businessPartnerSelectionsDelivery: businessPartnerRestorationData.delivery,
          案件編集で搬入先が表示されない場合: 'このログを確認してください',
        });

        setBusinessPartnerSelections(businessPartnerRestorationData);

        setForceUpdateKey(prev => prev + 1);
      } else if (isEditMode && caseId) {
        logger.info('[CaseFormPage] 編集モード - 案件データが見つかりません:', caseId);
      }
    }
  }, [loading, isEditMode, caseId, getCaseById, getBusinessPartnerById, businessPartners]);

  // 新規作成時にDateContextの日付が変更された場合の更新
  useEffect(() => {
    if (!isEditMode) {
      setFormData(prev => ({
        ...prev,
        pickupDate: selectedDate,
        deliveryDate: selectedDate,
      }));
    }
  }, [selectedDate, isEditMode]);

  // 案件作成後の処理
  useEffect(() => {
    if (formData.assignedLandTransporterId) {
      logger.info('[CaseFormPage] 陸送員アサイン処理:', formData.assignedLandTransporterId);

      const updatedData = {
        ...formData,
        status: 'assigned' as const,
        assignedLandTransporterId: formData.assignedLandTransporterId,
      };

      logger.info('[CaseFormPage] 案件ステータス更新:', updatedData);
    }
  }, [formData.assignedLandTransporterId, formData]);

  // ESCキーでAI提案を閉じる（統一キーボード管理）
  useEffect(() => {
    const unregister = registerHandler({
      id: 'case-form-page-escape',
      priority: KEYBOARD_PRIORITIES.PAGE_SHORTCUTS,
      keys: ['Escape'],
      shouldIgnoreInputFocus: true, // 入力フィールドフォーカス時も動作
      handler: (event: KeyboardEvent) => {
        if (event.key === 'Escape' && showSuggestions) {
          setShowSuggestions(false);
          setSuggestedTransporters([]);
          setSuggestionError('');
          return true; // 処理済み
        }
        return false; // 未処理
      },
    });

    return unregister;
  }, [
    showSuggestions,
    registerHandler,
    setShowSuggestions,
    setSuggestedTransporters,
    setSuggestionError,
  ]);

  // ウィンドウフォーカス時のフォームデータ再初期化（他のページでの変更を反映）
  useEffect(() => {
    const handleFocus = () => {
      if (isEditMode && caseId) {
        logger.info('[CaseFormPage] ウィンドウフォーカス - データ再初期化:', caseId);
        const existingCase = getCaseById(caseId);
        if (existingCase) {
          logger.info('[CaseFormPage] 最新の案件データで再初期化:', {
            id: existingCase.id,
            assignedLandTransporterId: existingCase.assignedLandTransporterId,
            pickupTimeStart: existingCase.pickupTimeStart,
            pickupTimeEnd: existingCase.pickupTimeEnd,
            deliveryTime: existingCase.deliveryTime,
          });

          // フォーカス時も初期化時と同じ処理を使用
          const updatedData = {
            ...existingCase,
            pickupDate: existingCase.pickupDate
              ? new Date(existingCase.pickupDate).toISOString().split('T')[0]
              : '',
            deliveryDate: existingCase.deliveryDate
              ? new Date(existingCase.deliveryDate).toISOString().split('T')[0]
              : '',
            // 時間フィールドを HH:mm 形式に変換（HH:mm:ss → HH:mm）
            pickupTimeStart: formatTimeToHHMM(existingCase.pickupTimeStart),
            pickupTimeEnd: formatTimeToHHMM(existingCase.pickupTimeEnd),
            deliveryTime: formatTimeToHHMM(existingCase.deliveryTime),
          };

          // FL-132: フォーカス時も住所が空の場合、取引先から自動設定
          if (updatedData.pickupPartnerId && !updatedData.pickupAddress) {
            const pickupPartner = getBusinessPartnerById(updatedData.pickupPartnerId);
            if (pickupPartner) {
              const defaultLocation = pickupPartner.locations?.find(loc => loc.isDefault);
              if (defaultLocation) {
                updatedData.pickupAddress = defaultLocation.address;
                updatedData.pickupPhone = defaultLocation.phone;
              } else if (pickupPartner.defaultAddress) {
                updatedData.pickupAddress = pickupPartner.defaultAddress;
                updatedData.pickupPhone = pickupPartner.defaultPhone || '';
              }
            }
          }

          if (updatedData.deliveryPartnerId && !updatedData.deliveryAddress) {
            const deliveryPartner = getBusinessPartnerById(updatedData.deliveryPartnerId);
            if (deliveryPartner) {
              const defaultLocation = deliveryPartner.locations?.find(loc => loc.isDefault);
              if (defaultLocation) {
                updatedData.deliveryAddress = defaultLocation.address;
                updatedData.deliveryPhone = defaultLocation.phone;
              } else if (deliveryPartner.defaultAddress) {
                updatedData.deliveryAddress = deliveryPartner.defaultAddress;
                updatedData.deliveryPhone = deliveryPartner.defaultPhone || '';
              }
            }
          }

          setFormData(updatedData);

          // BusinessPartnerSelectionsも復元（デバッグログ追加）
          const focusRestorationData = {
            billing: existingCase.billingPartnerId
              ? {
                  partnerId: existingCase.billingPartnerId,
                  locationId: existingCase.billingLocationId,
                }
              : undefined,
            pickup: existingCase.pickupPartnerId
              ? {
                  partnerId: existingCase.pickupPartnerId,
                  locationId: existingCase.pickupLocationId,
                }
              : undefined,
            delivery: existingCase.deliveryPartnerId
              ? {
                  partnerId: existingCase.deliveryPartnerId,
                  locationId: existingCase.deliveryLocationId,
                }
              : undefined,
          };

          logger.info(
            '[CaseFormPage] フォーカス時 - BusinessPartner選択状態復元:',
            focusRestorationData
          );
          setBusinessPartnerSelections(focusRestorationData);

          // フォーカス時の再初期化を強制更新として記録
          setForceUpdateKey(prev => prev + 1);
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isEditMode, caseId]);

  // URLパラメータの変更を監視して確実にデータを更新
  useEffect(() => {
    if (isEditMode && caseId) {
      logger.info('[CaseFormPage] URLパラメータ変更検知 - 強制更新:', caseId);
      setForceUpdateKey(prev => prev + 1);
    }
  }, [isEditMode, caseId]);

  // デバッグ: formDataの時間フィールド変更を監視
  useEffect(() => {
    logger.info('[CaseFormPage] formData時間フィールド監視:', {
      pickupTimeStart: formData.pickupTimeStart,
      pickupTimeEnd: formData.pickupTimeEnd,
      deliveryTime: formData.deliveryTime,
      timestamp: new Date().toISOString(),
    });
  }, [formData.pickupTimeStart, formData.pickupTimeEnd, formData.deliveryTime]);

  // FL-110: 締切違反チェック機能
  useEffect(() => {
    const checkDeadlineViolations = async () => {
      if (!companyId || (!formData.pickupPartnerId && !formData.deliveryPartnerId)) {
        setDeadlineWarnings({});
        return;
      }

      const warnings: { pickup?: DeadlineCheckResult; delivery?: DeadlineCheckResult } = {};

      try {
        // 引取先の締切チェック
        if (formData.pickupPartnerId && formData.pickupDate && formData.pickupTimeStart) {
          const pickupPartner = getBusinessPartnerById(formData.pickupPartnerId);
          if (pickupPartner) {
            const pickupDateTime = `${formData.pickupDate}T${formData.pickupTimeStart}:00.000Z`;
            const pickupCheck = await checkDeadline(
              formData.pickupPartnerId,
              pickupPartner.name,
              DeadlineRuleType.PICKUP,
              pickupDateTime,
              companyId
            );
            if (pickupCheck.isViolation) {
              warnings.pickup = pickupCheck;
            }
          }
        }

        // 搬入先の締切チェック
        if (formData.deliveryPartnerId && formData.deliveryDate && formData.deliveryTime) {
          const deliveryPartner = getBusinessPartnerById(formData.deliveryPartnerId);
          if (deliveryPartner) {
            const deliveryDateTime = `${formData.deliveryDate}T${formData.deliveryTime}:00.000Z`;
            const deliveryCheck = await checkDeadline(
              formData.deliveryPartnerId,
              deliveryPartner.name,
              DeadlineRuleType.DELIVERY,
              deliveryDateTime,
              companyId
            );
            if (deliveryCheck.isViolation) {
              warnings.delivery = deliveryCheck;
            }
          }
        }

        setDeadlineWarnings(warnings);
      } catch (error) {
        logger.error('[CaseFormPage] 締切違反チェックエラー:', error);
      }
    };

    checkDeadlineViolations();
  }, [
    formData.pickupPartnerId,
    formData.deliveryPartnerId,
    formData.pickupDate,
    formData.pickupTimeStart,
    formData.deliveryDate,
    formData.deliveryTime,
    companyId,
  ]);

  // FL-28: BusinessPartner選択のハンドラー関数
  const handleBusinessPartnerChange = (
    role: 'billing' | 'pickup' | 'delivery',
    selection: { partnerId: string; locationId?: string } | null
  ) => {
    logger.info('[FL-132] handleBusinessPartnerChange呼び出し:', { role, selection });

    setBusinessPartnerSelections(prev => ({
      ...prev,
      [role]: selection,
    }));

    // 最新の取引先選択状態を記録（非同期処理競合防止）
    if (role === 'pickup' || role === 'delivery') {
      latestPartnerSelection.current[role] = selection?.partnerId || null;
      logger.info(`[DeadlineRule] 最新選択状態記録: ${role} = ${selection?.partnerId || 'null'}`);
    }

    // formData のBusinessPartner関連フィールドを更新
    if (!isMounted.current) return;

    if (selection) {
      if (role === 'pickup' || role === 'delivery') {
        // pickup/delivery の場合は住所・電話番号も自動設定
        const partner = getBusinessPartnerById(selection.partnerId);
        logger.info('[FL-132] 取引先データ取得:', {
          partnerId: selection.partnerId,
          partner: partner?.name,
        });

        let address = '';
        let phone = '';

        if (partner) {
          // 特定の拠点が選択されている場合はその拠点の情報を使用
          if (selection.locationId) {
            const location = getBusinessPartnerLocationById(
              selection.partnerId,
              selection.locationId
            );
            if (location) {
              address = location.address;
              phone = location.phone;
              logger.info('[FL-132] 拠点住所設定:', {
                locationId: selection.locationId,
                address,
                phone,
              });
            }
          } else {
            // locationIdが未指定の場合はデフォルト拠点を使用
            const defaultLocation = partner.locations?.find(loc => loc.isDefault);
            if (defaultLocation) {
              address = defaultLocation.address;
              phone = defaultLocation.phone;
              logger.info('[FL-132] デフォルト拠点住所設定:', { address, phone });
            } else {
              // デフォルト拠点がない場合はpartnerのdefault情報をフォールバック
              address = partner.defaultAddress || '';
              phone = partner.defaultPhone || '';
              logger.info('[FL-132] パートナーデフォルト住所設定:', { address, phone });
            }
          }
        }

        logger.info('[FL-132] 最終設定値:', { role, address, phone });
        setFormData(prev => {
          const newData = {
            ...prev,
            [`${role}PartnerId`]: selection.partnerId,
            [`${role}LocationId`]: selection.locationId || null,
            [`${role}Address`]: address,
            [`${role}Phone`]: phone,
          };
          logger.info('[FL-132] フォームデータ更新完了:', {
            role,
            addressField: `${role}Address`,
            newAddress: newData[`${role}Address`],
            phoneField: `${role}Phone`,
            newPhone: newData[`${role}Phone`],
          });
          return newData;
        });

        // 締切ルール自動適用処理
        const applyDeadlineRule = async () => {
          try {
            // 引取先選択時の処理
            if (role === 'pickup' && formData.pickupDate) {
              // ローディング状態を開始
              setDeadlineRuleLoading(prev => ({ ...prev, pickup: true }));

              // 非同期処理開始前に最新の選択状態をチェック
              const currentSelection = latestPartnerSelection.current.pickup;
              if (currentSelection !== selection.partnerId) {
                logger.info('[DeadlineRule] 引取先選択が変更されたため処理をスキップ:', {
                  originalSelection: selection.partnerId,
                  currentSelection,
                });
                setDeadlineRuleLoading(prev => ({ ...prev, pickup: false }));
                return;
              }

              const pickupDate = new Date(formData.pickupDate);
              const pickupDayOfWeek = pickupDate.getDay() as DayOfWeek;

              logger.info('[DeadlineRule] 引取先締切ルール適用開始:', {
                partnerId: selection.partnerId,
                pickupDate: formData.pickupDate,
                dayOfWeek: pickupDayOfWeek,
                dayName: ['日', '月', '火', '水', '木', '金', '土'][pickupDayOfWeek],
              });

              // キャッシュから締切ルールを取得、なければDBから取得
              let rules: DeadlineRule[] = deadlineRulesCache.current[selection.partnerId];
              if (!rules) {
                logger.info(
                  '[DeadlineRule] キャッシュにないため、DBから取得:',
                  selection.partnerId
                );
                rules = await getDeadlineRulesByBP(selection.partnerId, companyId);
                // キャッシュに保存
                deadlineRulesCache.current[selection.partnerId] = rules;
                logger.info('[DeadlineRule] キャッシュに保存完了:', {
                  partnerId: selection.partnerId,
                  rulesCount: rules.length,
                });
              } else {
                logger.info('[DeadlineRule] キャッシュから取得:', {
                  partnerId: selection.partnerId,
                  rulesCount: rules.length,
                });
              }

              // 引取（PICKUP）ルールのうちアクティブなものを採用（曜日は許容）
              // 受け取るデータの大文字・小文字揺れに耐性を持たせる
              const applicableRule = rules.find(
                rule =>
                  String(rule.ruleType).toLowerCase() === DeadlineRuleType.PICKUP && rule.isActive
              );

              if (applicableRule) {
                // 締切ルール適用直前に再度最新選択状態をチェック
                const finalSelection = latestPartnerSelection.current.pickup;
                if (finalSelection !== selection.partnerId) {
                  logger.info(
                    '[DeadlineRule] 引取先選択が変更されたため適用をスキップ（適用直前）:',
                    {
                      originalSelection: selection.partnerId,
                      finalSelection,
                    }
                  );
                  return;
                }

                logger.info('[DeadlineRule] 引取締切ルール適用:', {
                  ruleId: applicableRule.id,
                  deadlineTime: applicableRule.deadlineTime,
                  dayOfWeek: applicableRule.dayOfWeek,
                  partnerName: partner?.name,
                });

                // バッファ時間設定を取得（デフォルトは30分）
                const bufferMinutes =
                  companyTimeSettings?.bufferTimeSettings?.betweenCasesBuffer || 30;

                // 引取開始時間から終了時間を計算（バッファ時間を加算）
                const startTime = applicableRule.deadlineTime;
                const [hours, minutes] = startTime.split(':').map(Number);
                const endDate = new Date();
                endDate.setHours(hours, minutes + bufferMinutes);
                const endHours = endDate.getHours();
                const endMinutes = endDate.getMinutes();
                const calculatedEndTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;

                logger.info('[DeadlineRule] バッファ時間を考慮した引取終了時間計算:', {
                  startTime,
                  bufferMinutes,
                  calculatedEndTime,
                });

                // 締切時間を引取開始時間として自動設定、終了時間はバッファ時間を加算
                setFormData(prev => ({
                  ...prev,
                  pickupTimeStart: startTime,
                  // 締切ルール適用時は常に終了時間を更新
                  pickupTimeEnd: calculatedEndTime,
                }));

                // 手動変更フラグをリセット（締切ルール適用のため）
                setManuallyOverridden(prev => ({
                  ...prev,
                  pickupTimeStart: false,
                  pickupTimeEnd: false,
                }));

                // 適用状態を記録
                setAppliedDeadlineRules(prev => ({
                  ...prev,
                  pickup: {
                    rule: applicableRule,
                    dayOfWeek: pickupDayOfWeek,
                    partnerName: partner?.name || '',
                  },
                }));

                logger.info('[DeadlineRule] 引取締切ルール適用完了');
              } else {
                logger.info('[DeadlineRule] 適用可能な引取締切ルールが見つかりませんでした');
                // 適用状態をクリア
                setAppliedDeadlineRules(prev => ({
                  ...prev,
                  pickup: undefined,
                }));
                // 編集モード時は既存の時間値を保持（新規作成時のみクリア）
                if (!isEditMode) {
                  setFormData(prev => ({
                    ...prev,
                    pickupTimeStart: '',
                    pickupTimeEnd: '',
                  }));
                }
              }

              // ローディング状態を終了
              setDeadlineRuleLoading(prev => ({ ...prev, pickup: false }));
            }

            // 搬入先選択時の処理
            if (role === 'delivery' && formData.deliveryDate) {
              // ローディング状態を開始
              setDeadlineRuleLoading(prev => ({ ...prev, delivery: true }));

              // 非同期処理開始前に最新の選択状態をチェック
              const currentSelection = latestPartnerSelection.current.delivery;
              if (currentSelection !== selection.partnerId) {
                logger.info('[DeadlineRule] 搬入先選択が変更されたため処理をスキップ:', {
                  originalSelection: selection.partnerId,
                  currentSelection,
                });
                setDeadlineRuleLoading(prev => ({ ...prev, delivery: false }));
                return;
              }

              const deliveryDate = new Date(formData.deliveryDate);
              const deliveryDayOfWeek = deliveryDate.getDay() as DayOfWeek;

              logger.info('[DeadlineRule] 搬入先締切ルール適用開始:', {
                partnerId: selection.partnerId,
                deliveryDate: formData.deliveryDate,
                dayOfWeek: deliveryDayOfWeek,
                dayName: ['日', '月', '火', '水', '木', '金', '土'][deliveryDayOfWeek],
              });

              // キャッシュから締切ルールを取得、なければDBから取得
              let rules: DeadlineRule[] = deadlineRulesCache.current[selection.partnerId];
              if (!rules) {
                logger.info(
                  '[DeadlineRule] キャッシュにないため、DBから取得:',
                  selection.partnerId
                );
                rules = await getDeadlineRulesByBP(selection.partnerId, companyId);
                // キャッシュに保存
                deadlineRulesCache.current[selection.partnerId] = rules;
                logger.info('[DeadlineRule] キャッシュに保存完了:', {
                  partnerId: selection.partnerId,
                  rulesCount: rules.length,
                });
              } else {
                logger.info('[DeadlineRule] キャッシュから取得:', {
                  partnerId: selection.partnerId,
                  rulesCount: rules.length,
                });
              }

              // 搬入（DELIVERY）ルールのうちアクティブなものを採用（曜日は許容）
              // 受け取るデータの大文字・小文字揺れに耐性を持たせる
              const applicableRule = rules.find(
                rule =>
                  String(rule.ruleType).toLowerCase() === DeadlineRuleType.DELIVERY && rule.isActive
              );

              if (applicableRule) {
                // 締切ルール適用直前に再度最新選択状態をチェック
                const finalSelection = latestPartnerSelection.current.delivery;
                if (finalSelection !== selection.partnerId) {
                  logger.info(
                    '[DeadlineRule] 搬入先選択が変更されたため適用をスキップ（適用直前）:',
                    {
                      originalSelection: selection.partnerId,
                      finalSelection,
                    }
                  );
                  return;
                }

                logger.info('[DeadlineRule] 搬入締切ルール適用:', {
                  ruleId: applicableRule.id,
                  deadlineTime: applicableRule.deadlineTime,
                  dayOfWeek: applicableRule.dayOfWeek,
                  partnerName: partner?.name,
                });

                // 締切時間を搬入時間として自動設定
                setFormData(prev => ({
                  ...prev,
                  deliveryTime: applicableRule.deadlineTime,
                }));

                // 手動変更フラグをリセット（締切ルール適用のため）
                setManuallyOverridden(prev => ({
                  ...prev,
                  deliveryTime: false,
                }));

                // 適用状態を記録
                setAppliedDeadlineRules(prev => ({
                  ...prev,
                  delivery: {
                    rule: applicableRule,
                    dayOfWeek: deliveryDayOfWeek,
                    partnerName: partner?.name || '',
                  },
                }));

                logger.info('[DeadlineRule] 搬入締切ルール適用完了');
              } else {
                logger.info('[DeadlineRule] 適用可能な搬入締切ルールが見つかりませんでした');
                // 適用状態をクリア
                setAppliedDeadlineRules(prev => ({
                  ...prev,
                  delivery: undefined,
                }));
                // 編集モード時は既存の時間値を保持（新規作成時のみクリア）
                if (!isEditMode) {
                  setFormData(prev => ({
                    ...prev,
                    deliveryTime: '',
                  }));
                }
              }

              // ローディング状態を終了
              setDeadlineRuleLoading(prev => ({ ...prev, delivery: false }));
            }
          } catch (error) {
            logger.error('[DeadlineRule] 締切ルール適用エラー:', error);
            // エラー時もローディング状態を終了
            setDeadlineRuleLoading(prev => ({
              ...prev,
              pickup: role === 'pickup' ? false : prev.pickup,
              delivery: role === 'delivery' ? false : prev.delivery,
            }));
            // エラーでも処理は継続（住所設定などは成功しているため）
          }
        };

        // 締切ルール適用を実行
        applyDeadlineRule();
      } else {
        // billing の場合は PartnerId と LocationId のみ更新
        setFormData(prev => ({
          ...prev,
          [`${role}PartnerId`]: selection.partnerId,
          [`${role}LocationId`]: selection.locationId || null,
        }));
      }
    } else {
      // BusinessPartnerが選択されていない場合（自由入力モード）は関連フィールドをクリア
      setFormData(prev => {
        const updates: any = {
          [`${role}PartnerId`]: null,
          [`${role}LocationId`]: null,
        };

        // 自由入力モード時：pickup/delivery の場合は住所・電話もクリア（ユーザーが自由に入力可能にする）
        if (role === 'pickup' || role === 'delivery') {
          updates[`${role}Address`] = '';
          updates[`${role}Phone`] = '';
        }

        return {
          ...prev,
          ...updates,
        };
      });

      // 適用された締切ルール状態もクリア
      if (role === 'pickup' || role === 'delivery') {
        // 最新選択状態をnullに更新（非同期処理の競合防止）
        latestPartnerSelection.current[role] = null;

        // ローディング状態もクリア
        setDeadlineRuleLoading(prev => ({ ...prev, [role]: false }));

        // 締切ルール適用状態を即座にクリア
        setAppliedDeadlineRules(prev => ({
          ...prev,
          [role]: undefined,
        }));
        logger.info(`[DeadlineRule] ${role}の締切ルール適用状態を即座にクリアしました（選択解除）`);
      }
    }
  };

  // 日付変更時の締切ルール再計算
  useEffect(() => {
    const recalculatePickupDeadline = async () => {
      if (!formData.pickupPartnerId || !formData.pickupDate) return;
      const initialPartnerId = formData.pickupPartnerId;

      try {
        const partner = getBusinessPartnerById(formData.pickupPartnerId);
        const pickupDate = new Date(formData.pickupDate);
        const pickupDayOfWeek = pickupDate.getDay() as DayOfWeek;

        logger.info('[DeadlineRule] 引取日変更による締切ルール再計算:', {
          partnerId: formData.pickupPartnerId,
          pickupDate: formData.pickupDate,
          dayOfWeek: pickupDayOfWeek,
          dayName: ['日', '月', '火', '水', '木', '金', '土'][pickupDayOfWeek],
        });

        // キャッシュから締切ルールを取得、なければDBから取得
        let rules: DeadlineRule[] = deadlineRulesCache.current[formData.pickupPartnerId];
        if (!rules) {
          logger.info(
            '[DeadlineRule] 日付変更：キャッシュにないため、DBから取得:',
            formData.pickupPartnerId
          );
          rules = await getDeadlineRulesByBP(formData.pickupPartnerId, companyId);
          // キャッシュに保存
          deadlineRulesCache.current[formData.pickupPartnerId] = rules;
          logger.info('[DeadlineRule] 日付変更：キャッシュに保存完了:', {
            partnerId: formData.pickupPartnerId,
            rulesCount: rules.length,
          });
        } else {
          logger.info('[DeadlineRule] 日付変更：キャッシュから取得:', {
            partnerId: formData.pickupPartnerId,
            rulesCount: rules.length,
          });
        }

        // 引取（PICKUP）ルールのうちアクティブなものを採用（曜日は許容）
        // 受け取るデータの大文字・小文字揺れに耐性を持たせる
        const applicableRule = rules.find(
          rule => String(rule.ruleType).toLowerCase() === DeadlineRuleType.PICKUP && rule.isActive
        );

        if (applicableRule) {
          // 最新の選択が変更/解除されていないか確認（キャンセル対応）
          const currentSelectionState = businessPartnerSelections.pickup?.partnerId || null;
          const latest = latestPartnerSelection.current.pickup;
          // キャンセル（latest=null かつ state=null）または別選択に変わっている場合はスキップ
          if (
            (latest === null && currentSelectionState === null) ||
            (latest !== null && latest !== initialPartnerId)
          ) {
            logger.info('[DeadlineRule] 引取日変更：選択変更/解除のため適用スキップ', {
              initialPartnerId,
              latest,
              currentSelectionState,
            });
            return;
          }
          logger.info('[DeadlineRule] 引取日変更による締切ルール適用:', {
            ruleId: applicableRule.id,
            deadlineTime: applicableRule.deadlineTime,
            dayOfWeek: applicableRule.dayOfWeek,
          });

          // バッファ時間設定を取得（デフォルトは30分）
          const bufferMinutes = companyTimeSettings?.bufferTimeSettings?.betweenCasesBuffer || 30;

          // 引取開始時間から終了時間を計算（バッファ時間を加算）
          const startTime = applicableRule.deadlineTime;
          const [hours, minutes] = startTime.split(':').map(Number);
          const endDate = new Date();
          endDate.setHours(hours, minutes + bufferMinutes);
          const endHours = endDate.getHours();
          const endMinutes = endDate.getMinutes();
          const calculatedEndTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;

          logger.info('[DeadlineRule] 引取日変更時のバッファ時間を考慮した引取終了時間計算:', {
            startTime,
            bufferMinutes,
            calculatedEndTime,
          });

          // 締切時間を引取開始時間として自動設定、終了時間はバッファ時間を加算
          setFormData(prev => ({
            ...prev,
            // 日付変更時は常に締切ルールを適用
            pickupTimeStart: startTime,
            pickupTimeEnd: calculatedEndTime,
          }));

          // 手動変更フラグをリセット（日付変更による締切ルール再適用のため）
          setManuallyOverridden(prev => ({
            ...prev,
            pickupTimeStart: false,
            pickupTimeEnd: false,
          }));

          // 適用状態を記録
          setAppliedDeadlineRules(prev => ({
            ...prev,
            pickup: {
              rule: applicableRule,
              dayOfWeek: pickupDayOfWeek,
              partnerName: partner?.name || '',
            },
          }));
        } else {
          logger.info('[DeadlineRule] 引取日変更：適用可能な締切ルールが見つかりませんでした');
          // 適用状態をクリア
          setAppliedDeadlineRules(prev => ({
            ...prev,
            pickup: undefined,
          }));
          // 編集モード時は既存の時間値を保持（新規作成時のみクリア）
          if (!isEditMode) {
            setFormData(prev => ({
              ...prev,
              pickupTimeStart: '',
              pickupTimeEnd: '',
            }));
          }
        }
      } catch (error) {
        logger.error('[DeadlineRule] 引取日変更による締切ルール再計算エラー:', error);
      }
    };

    recalculatePickupDeadline();
  }, [formData.pickupDate, formData.pickupPartnerId, companyId, getBusinessPartnerById]);

  // 搬入日変更時の締切ルール再計算
  useEffect(() => {
    const recalculateDeliveryDeadline = async () => {
      if (!formData.deliveryPartnerId || !formData.deliveryDate) return;
      const initialPartnerId = formData.deliveryPartnerId;

      try {
        const partner = getBusinessPartnerById(formData.deliveryPartnerId);
        const deliveryDate = new Date(formData.deliveryDate);
        const deliveryDayOfWeek = deliveryDate.getDay() as DayOfWeek;

        logger.info('[DeadlineRule] 搬入日変更による締切ルール再計算:', {
          partnerId: formData.deliveryPartnerId,
          deliveryDate: formData.deliveryDate,
          dayOfWeek: deliveryDayOfWeek,
          dayName: ['日', '月', '火', '水', '木', '金', '土'][deliveryDayOfWeek],
        });

        // キャッシュから締切ルールを取得、なければDBから取得
        let rules: DeadlineRule[] = deadlineRulesCache.current[formData.deliveryPartnerId];
        if (!rules) {
          logger.info(
            '[DeadlineRule] 日付変更：キャッシュにないため、DBから取得:',
            formData.deliveryPartnerId
          );
          rules = await getDeadlineRulesByBP(formData.deliveryPartnerId, companyId);
          // キャッシュに保存
          deadlineRulesCache.current[formData.deliveryPartnerId] = rules;
          logger.info('[DeadlineRule] 日付変更：キャッシュに保存完了:', {
            partnerId: formData.deliveryPartnerId,
            rulesCount: rules.length,
          });
        } else {
          logger.info('[DeadlineRule] 日付変更：キャッシュから取得:', {
            partnerId: formData.deliveryPartnerId,
            rulesCount: rules.length,
          });
        }

        // 搬入（DELIVERY）ルールのうちアクティブなものを採用（曜日は許容）
        // 受け取るデータの大文字・小文字揺れに耐性を持たせる
        const applicableRule = rules.find(
          rule => String(rule.ruleType).toLowerCase() === DeadlineRuleType.DELIVERY && rule.isActive
        );

        if (applicableRule) {
          const currentDeliveryState = businessPartnerSelections.delivery?.partnerId || null;
          const latestD = latestPartnerSelection.current.delivery;
          if (
            (latestD === null && currentDeliveryState === null) ||
            (latestD !== null && latestD !== initialPartnerId)
          ) {
            logger.info('[DeadlineRule] 搬入日変更：選択変更/解除のため適用スキップ', {
              initialPartnerId,
              latest: latestD,
              currentSelectionState: currentDeliveryState,
            });
            return;
          }
          logger.info('[DeadlineRule] 搬入日変更による締切ルール適用:', {
            ruleId: applicableRule.id,
            deadlineTime: applicableRule.deadlineTime,
            dayOfWeek: applicableRule.dayOfWeek,
          });

          // 締切時間を搬入時間として自動設定
          setFormData(prev => ({
            ...prev,
            // 日付変更時は常に締切ルールを適用
            deliveryTime: applicableRule.deadlineTime,
          }));

          // 手動変更フラグをリセット（日付変更による締切ルール再適用のため）
          setManuallyOverridden(prev => ({
            ...prev,
            deliveryTime: false,
          }));

          // 適用状態を記録
          setAppliedDeadlineRules(prev => ({
            ...prev,
            delivery: {
              rule: applicableRule,
              dayOfWeek: deliveryDayOfWeek,
              partnerName: partner?.name || '',
            },
          }));
        } else {
          logger.info('[DeadlineRule] 搬入日変更：適用可能な締切ルールが見つかりませんでした');
          // 適用状態をクリア
          setAppliedDeadlineRules(prev => ({
            ...prev,
            delivery: undefined,
          }));
          // 編集モード時は既存の時間値を保持（新規作成時のみクリア）
          if (!isEditMode) {
            setFormData(prev => ({
              ...prev,
              deliveryTime: '',
            }));
          }
        }
      } catch (error) {
        logger.error('[DeadlineRule] 搬入日変更による締切ルール再計算エラー:', error);
      }
    };

    recalculateDeliveryDeadline();
  }, [formData.deliveryDate, formData.deliveryPartnerId, companyId, getBusinessPartnerById]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value, type } = e.target;

      // 🔍 陸送員割り当て変更の詳細ログ
      if (name === 'assignedLandTransporterId') {
        logger.info(`[TRANSPORTER-DEBUG] 📝 セレクト変更検知`, {
          newValue: value,
          valueType: typeof value,
          isEmpty: !value,
          currentFormDataValue: formData.assignedLandTransporterId,
        });
      }

      // 時間フィールドの手動変更を検知
      if (name === 'pickupTimeStart' || name === 'pickupTimeEnd' || name === 'deliveryTime') {
        setManuallyOverridden(prev => ({
          ...prev,
          [name]: true,
        }));
        logger.info(`[DeadlineRule] 時間フィールド手動変更検知: ${name} = ${value}`);
      }

      const updatedData = {
        ...formData,
        [name]: type === 'number' ? Number(value) : value,
      };

      // 🔍 陸送員割り当て更新後の状態ログ
      if (name === 'assignedLandTransporterId') {
        logger.info(`[TRANSPORTER-DEBUG] 📝 setFormData実行直前`, {
          assignedLandTransporterId: updatedData.assignedLandTransporterId,
          type: typeof updatedData.assignedLandTransporterId,
        });
      }

      // 引き取り先顧客が変更された場合、住所と電話番号を自動設定
      if (name === 'pickupClientId' && value) {
        const selectedClient = clients.find(client => client.id === value);
        if (selectedClient) {
          updatedData.pickupAddress = selectedClient.address;
          updatedData.pickupPhone = selectedClient.phone;
        }
      }

      // 搬入先顧客が変更された場合、住所と電話番号を自動設定
      if (name === 'deliveryClientId' && value) {
        const selectedClient = clients.find(client => client.id === value);
        if (selectedClient) {
          updatedData.deliveryAddress = selectedClient.address;
          updatedData.deliveryPhone = selectedClient.phone;
        }
      }

      setFormData(updatedData);

      // 🔍 setFormData実行後のフォームデータログ
      if (name === 'assignedLandTransporterId') {
        logger.info(`🚨 [陸送員更新] setFormData実行後`, {
          message: 'formDataは非同期更新のため、直後の読み込みは古い値',
        });
      }
    },
    [formData, clients]
  );

  // FL-67: 引取先取引先用OCR結果処理（未使用）
  // const _handlePickupPartnerOCRResultsChange = useCallback((results: ImageOCRResult[]) => {
  //   setPickupPartnerOCRResults(results);

  //   const allAddresses: ExtractedAddress[] = [];
  //   results.forEach(result => {
  //     if (result.extractedData && result.extractedData.length > 0) {
  //       allAddresses.push(...result.extractedData);
  //     }
  //   });

  //   // 自動判定を実行（引取先取引先用コンテキストを追加）
  //   const detectionResults = detectMultipleAddressTypes(allAddresses, '引取先 取引先 会社 営業所');
  //   const addressesWithDetection = detectionResults.map(r => ({
  //     ...r.address,
  //     detectedType: r.type,
  //     detectionConfidence: r.confidence,
  //     detectionReason: r.reason,
  //   }));

  //   setPickupPartnerAddresses(addressesWithDetection);
  //   setShowPickupPartnerSelection(addressesWithDetection.length > 0);
  // }, []);

  // FL-67: 引取先取引先住所選択のハンドラー（未使用）
  // const _handlePickupPartnerAddressSelect = useCallback((address: ExtractedAddress) => {
  //   logger.info(`[CaseFormPage] 引取先取引先住所を選択: ${address.fullAddress}`);
  //   // TODO: 将来的にBusinessPartner自動作成機能を実装
  //   alert(
  //     `引取先取引先住所が抽出されました: ${address.fullAddress}\n\nBusinessPartner自動作成機能は今後実装予定です。`
  //   );
  //   setShowPickupPartnerSelection(false);
  // }, []);

  // FL-67: 搬入先取引先用OCR結果処理（未使用）
  // const _handleDeliveryPartnerOCRResultsChange = useCallback((results: ImageOCRResult[]) => {
  //   setDeliveryPartnerOCRResults(results);

  //   const allAddresses: ExtractedAddress[] = [];
  //   results.forEach(result => {
  //     if (result.extractedData && result.extractedData.length > 0) {
  //       allAddresses.push(...result.extractedData);
  //     }
  //   });

  //   // 自動判定を実行（搬入先取引先用コンテキストを追加）
  //   const detectionResults = detectMultipleAddressTypes(
  //     allAddresses,
  //     '搬入先 取引先 会社 営業所 配送先'
  //   );
  //   const addressesWithDetection = detectionResults.map(r => ({
  //     ...r.address,
  //     detectedType: r.type,
  //     detectionConfidence: r.confidence,
  //     detectionReason: r.reason,
  //   }));

  //   setDeliveryPartnerAddresses(addressesWithDetection);
  //   setShowDeliveryPartnerSelection(addressesWithDetection.length > 0);
  // }, []);

  // FL-67: 搬入先取引先住所選択のハンドラー（未使用）
  // const _handleDeliveryPartnerAddressSelect = useCallback((address: ExtractedAddress) => {
  //   logger.info(`[CaseFormPage] 搬入先取引先住所を選択: ${address.fullAddress}`);
  //   // TODO: 将来的にBusinessPartner自動作成機能を実装
  //   alert(
  //     `搬入先取引先住所が抽出されました: ${address.fullAddress}\n\nBusinessPartner自動作成機能は今後実装予定です。`
  //   );
  //   setShowDeliveryPartnerSelection(false);
  // }, []);

  // FL-67: 引取先住所用OCR結果処理
  const handlePickupAddressOCRResultsChange = useCallback((results: ImageOCRResult[]) => {
    setPickupAddressOCRResults(results);

    const allAddresses: ExtractedAddress[] = [];
    results.forEach(result => {
      if (result.extractedData && result.extractedData.length > 0) {
        allAddresses.push(...result.extractedData);
      }
    });

    // 自動判定を実行（引取先住所用コンテキストを追加）
    const detectionResults = detectMultipleAddressTypes(allAddresses, '引取 集荷 pickup 住所');
    const addressesWithDetection = detectionResults.map(r => ({
      ...r.address,
      detectedType: r.type,
      detectionConfidence: r.confidence,
      detectionReason: r.reason,
    }));

    setPickupAddressAddresses(addressesWithDetection);
    setShowPickupAddressSelection(addressesWithDetection.length > 0);
  }, []);

  // FL-67: 引取先住所選択のハンドラー
  const handlePickupAddressSelect = useCallback((address: ExtractedAddress) => {
    try {
      logger.info(`[CaseFormPage] 引取先住所を選択: ${address.fullAddress}`);

      // 住所を設定
      if (!isMounted.current) return;
      setFormData(prev => ({
        ...prev,
        pickupAddress: address.fullAddress,
      }));
      logger.info(`[CaseFormPage] 引取先住所を設定完了: ${address.fullAddress}`);

      // 元のテキストから電話番号を抽出して自動設定
      logger.info(`[CaseFormPage] 電話番号抽出開始 - 元テキスト: "${address.rawText}"`);
      const phoneNumbers = extractPhoneNumbersFromMultipleTexts([address.rawText]);
      logger.info(`[CaseFormPage] 抽出された電話番号: ${phoneNumbers.length}件`, phoneNumbers);

      if (phoneNumbers.length > 0) {
        const bestPhone = phoneNumbers[0]; // 最も信頼度の高い電話番号
        setTimeout(() => {
          if (!isMounted.current) return;
          setFormData(prev => ({
            ...prev,
            pickupPhone: bestPhone.phoneNumber,
          }));
        }, 0);
        logger.info(
          `[CaseFormPage] 引取先電話番号を自動設定: ${bestPhone.phoneNumber} (信頼度: ${Math.round(bestPhone.confidence * 100)}%)`
        );
      } else {
        logger.info(`[CaseFormPage] 電話番号が見つかりませんでした`);
      }

      setShowPickupAddressSelection(false);
    } catch (error) {
      logger.error('[CaseFormPage] 引取先住所選択エラー:', error);
      // エラーが発生しても住所だけは設定を試す（非同期でsetState）
      setTimeout(() => {
        if (!isMounted.current) return;
        setFormData(prev => ({
          ...prev,
          pickupAddress: address.fullAddress,
        }));
      }, 0);
      setShowPickupAddressSelection(false);
    }
  }, []);

  // FL-67: 搬入先住所用OCR結果処理
  const handleDeliveryAddressOCRResultsChange = useCallback((results: ImageOCRResult[]) => {
    setDeliveryAddressOCRResults(results);

    const allAddresses: ExtractedAddress[] = [];
    results.forEach(result => {
      if (result.extractedData && result.extractedData.length > 0) {
        allAddresses.push(...result.extractedData);
      }
    });

    // 自動判定を実行（搬入先住所用コンテキストを追加）
    const detectionResults = detectMultipleAddressTypes(
      allAddresses,
      '搬入 配送 delivery 住所 宛先'
    );
    const addressesWithDetection = detectionResults.map(r => ({
      ...r.address,
      detectedType: r.type,
      detectionConfidence: r.confidence,
      detectionReason: r.reason,
    }));

    setDeliveryAddressAddresses(addressesWithDetection);
    setShowDeliveryAddressSelection(addressesWithDetection.length > 0);
  }, []);

  // FL-67: 搬入先住所選択のハンドラー
  const handleDeliveryAddressSelect = useCallback((address: ExtractedAddress) => {
    try {
      logger.info(`[CaseFormPage] 搬入先住所を選択: ${address.fullAddress}`);

      // 住所を設定
      if (!isMounted.current) return;
      setFormData(prev => ({
        ...prev,
        deliveryAddress: address.fullAddress,
      }));
      logger.info(`[CaseFormPage] 搬入先住所を設定完了: ${address.fullAddress}`);

      // 元のテキストから電話番号を抽出して自動設定
      logger.info(`[CaseFormPage] 搬入先電話番号抽出開始 - 元テキスト: "${address.rawText}"`);
      const phoneNumbers = extractPhoneNumbersFromMultipleTexts([address.rawText]);
      logger.info(
        `[CaseFormPage] 搬入先抽出された電話番号: ${phoneNumbers.length}件`,
        phoneNumbers
      );

      if (phoneNumbers.length > 0) {
        const bestPhone = phoneNumbers[0]; // 最も信頼度の高い電話番号
        setTimeout(() => {
          if (!isMounted.current) return;
          setFormData(prev => ({
            ...prev,
            deliveryPhone: bestPhone.phoneNumber,
          }));
          logger.info(
            `[CaseFormPage] 搬入先電話番号を自動設定: ${bestPhone.phoneNumber} (信頼度: ${Math.round(bestPhone.confidence * 100)}%)`
          );
        }, 0);
      } else {
        logger.info(`[CaseFormPage] 搬入先電話番号が見つかりませんでした`);
      }

      setShowDeliveryAddressSelection(false);
    } catch (error) {
      logger.error('[CaseFormPage] 搬入先住所選択エラー:', error);
      // エラーが発生しても住所だけは設定を試す（非同期でsetState）
      setTimeout(() => {
        if (!isMounted.current) return;
        setFormData(prev => ({
          ...prev,
          deliveryAddress: address.fullAddress,
        }));
      }, 0);
      setShowDeliveryAddressSelection(false);
    }
  }, []);

  // FL-35: Google Maps API による距離計算ハンドラー（AddressAutocompleteInput用）
  const handleCalculateDistance = useCallback(async (pickupAddr: string, deliveryAddr: string) => {
    if (!pickupAddr || !deliveryAddr) {
      logger.warn('[CaseFormPage] 距離計算: 住所が不足しています');
      return;
    }

    try {
      logger.info('[CaseFormPage] 距離計算開始:', {
        pickupAddr,
        deliveryAddr,
      });

      // FL-170: 3層キャッシング戦略でルート情報を取得
      const result = await distanceCalculationService.calculate(pickupAddr, deliveryAddr, {
        forceRefresh: false,
      });

      if (result) {
        if (!isMounted.current) return;

        // フォームデータを更新
        setFormData(prev => ({
          ...prev,
          distance_km: Number((result.distance / 1000).toFixed(1)), // メートルからキロメートルに変換
          estimated_duration_minutes: Number((result.duration / 60).toFixed(1)), // 秒から分に変換
          distance_calculated_at: result.cachedAt,
        }));

        // ルート可視化データをstateに設定（distance_sourceはデータソースを追跡）
        setRouteVisualizationData({
          pickup_address: pickupAddr,
          delivery_address: deliveryAddr,
          distance_km: Number((result.distance / 1000).toFixed(1)),
          estimated_duration_minutes: Number((result.duration / 60).toFixed(1)),
          polyline_points: [] as Array<{ lat: number; lng: number }>,
          steps: [],
          calculated_at: result.cachedAt,
          distance_source: result.source, // キャッシュ/API/期限切れキャッシュを追跡
        });

        logger.info('[CaseFormPage] ルート計算完了:', {
          distance_km: result.distance / 1000,
          estimated_duration_minutes: result.duration / 60,
          source: result.source,
        });
      } else {
        logger.warn('[CaseFormPage] ルート計算失敗: 結果がnullです');
      }
    } catch (error) {
      logger.error('[CaseFormPage] ルート計算エラー:', error);
    }
  }, []);

  // FL-35: AddressAutocompleteInputの選択ハンドラー（引取先）
  const handlePickupAddressAutocompleteSelect = useCallback(
    (address: string, _placeId?: string) => {
      if (!isMounted.current) return;
      setFormData(prev => ({
        ...prev,
        pickupAddress: address,
      }));
      // 搬入先住所も入力済みなら距離計算を実行
      const deliveryAddr = formData.deliveryAddress;
      if (deliveryAddr) {
        handleCalculateDistance(address, deliveryAddr);
      }
      logger.info('[CaseFormPage] 引取先住所をAutocompleteで選択:', address);
    },
    [formData.deliveryAddress, handleCalculateDistance]
  );

  // FL-35: AddressAutocompleteInputの選択ハンドラー（搬入先）
  const handleDeliveryAddressAutocompleteSelect = useCallback(
    (address: string, _placeId?: string) => {
      if (!isMounted.current) return;
      setFormData(prev => ({
        ...prev,
        deliveryAddress: address,
      }));
      // 引取先住所も入力済みなら距離計算を実行
      const pickupAddr = formData.pickupAddress;
      if (pickupAddr) {
        handleCalculateDistance(pickupAddr, address);
      }
      logger.info('[CaseFormPage] 搬入先住所をAutocompleteで選択:', address);
    },
    [formData.pickupAddress, handleCalculateDistance]
  );

  // FL-67: 引取先取引先用OCRイベントハンドラー（未使用）
  // const _pickupPartnerOCREventHandlers: OCREventHandlers = useCallback(() => ({
  //   onProcessingStart: (fileName: string) => {
  //     logger.info(`[CaseFormPage] 引取先取引先用OCR処理開始: ${fileName}`);
  //   },
  //   onProcessingProgress: (fileName: string, progress: number) => {
  //     logger.info(
  //       `[CaseFormPage] 引取先取引先用OCR処理進捗: ${fileName} - ${Math.round(progress * 100)}%`
  //     );
  //   },
  //   onProcessingComplete: (result: ImageOCRResult) => {
  //     logger.info(`[CaseFormPage] 引取先取引先用OCR処理完了: ${result.fileName}`, result);
  //   },
  //   onProcessingError: (fileName: string, error: string) => {
  //     logger.error(`[CaseFormPage] 引取先取引先用OCR処理エラー: ${fileName}`, error);
  //     const errorMsg = error.includes('初期化')
  //       ? 'OCRシステムの初期化中です。しばらく待ってから再度お試しください。'
  //       : `引取先取引先住所の画像処理でエラーが発生しました: ${error}`;
  //     alert(`${fileName}\n${errorMsg}`);
  //   },
  //   onAddressExtracted: (addresses: ExtractedAddress[]) => {
  //     logger.info(`[CaseFormPage] 引取先取引先住所抽出完了: ${addresses.length}件`, addresses);
  //   },
  // }), []);

  // FL-67: 搬入先取引先用OCRイベントハンドラー（未使用）
  // const _deliveryPartnerOCREventHandlers: OCREventHandlers = useCallback(() => ({
  //   onProcessingStart: (fileName: string) => {
  //     logger.info(`[CaseFormPage] 搬入先取引先用OCR処理開始: ${fileName}`);
  //   },
  //   onProcessingProgress: (fileName: string, progress: number) => {
  //     logger.info(
  //       `[CaseFormPage] 搬入先取引先用OCR処理進捗: ${fileName} - ${Math.round(progress * 100)}%`
  //     );
  //   },
  //   onProcessingComplete: (result: ImageOCRResult) => {
  //     logger.info(`[CaseFormPage] 搬入先取引先用OCR処理完了: ${result.fileName}`, result);
  //   },
  //   onProcessingError: (fileName: string, error: string) => {
  //     logger.error(`[CaseFormPage] 搬入先取引先用OCR処理エラー: ${fileName}`, error);
  //     const errorMsg = error.includes('初期化')
  //       ? 'OCRシステムの初期化中です。しばらく待ってから再度お試しください。'
  //       : `搬入先取引先住所の画像処理でエラーが発生しました: ${error}`;
  //     alert(`${fileName}\n${errorMsg}`);
  //   },
  //   onAddressExtracted: (addresses: ExtractedAddress[]) => {
  //     logger.info(`[CaseFormPage] 搬入先取引先住所抽出完了: ${addresses.length}件`, addresses);
  //   },
  // }), []);

  // FL-67: 引取先住所用OCRイベントハンドラー
  const pickupAddressOCREventHandlers: OCREventHandlers = useMemo(
    () => ({
      onProcessingStart: (fileName: string) => {
        logger.info(`[CaseFormPage] 引取先住所用OCR処理開始: ${fileName}`);
      },
      onProcessingProgress: (fileName: string, progress: number) => {
        logger.info(
          `[CaseFormPage] 引取先住所用OCR処理進捗: ${fileName} - ${Math.round(progress * 100)}%`
        );
      },
      onProcessingComplete: (result: ImageOCRResult) => {
        logger.info(`[CaseFormPage] 引取先住所用OCR処理完了: ${result.fileName}`, result);
      },
      onProcessingError: (fileName: string, error: string) => {
        logger.error(`[CaseFormPage] 引取先住所用OCR処理エラー: ${fileName}`, error);
        const errorMsg = error.includes('初期化')
          ? 'OCRシステムの初期化中です。しばらく待ってから再度お試しください。'
          : `引取先住所の画像処理でエラーが発生しました: ${error}`;
        alert(`${fileName}\n${errorMsg}`);
      },
      onAddressExtracted: (addresses: ExtractedAddress[]) => {
        logger.info(`[CaseFormPage] 引取先住所抽出完了: ${addresses.length}件`, addresses);

        // 住所が抽出された場合、最初の住所を自動設定
        if (addresses.length > 0) {
          const firstAddress = addresses[0];
          logger.info(`[CaseFormPage] 引取先住所を自動設定: ${firstAddress.fullAddress}`);

          try {
            // 住所を自動設定（非同期でsetState）
            setTimeout(() => {
              if (!isMounted.current) return;
              setFormData(prev => ({
                ...prev,
                pickupAddress: firstAddress.fullAddress,
              }));
            }, 0);

            // 元のテキストから電話番号を抽出して自動設定
            logger.info(`[CaseFormPage] 電話番号抽出開始 - 元テキスト: "${firstAddress.rawText}"`);
            const phoneNumbers = extractPhoneNumbersFromMultipleTexts([firstAddress.rawText]);
            logger.info(
              `[CaseFormPage] 抽出された電話番号: ${phoneNumbers.length}件`,
              phoneNumbers
            );

            if (phoneNumbers.length > 0) {
              const bestPhone = phoneNumbers[0]; // 最も信頼度の高い電話番号
              setTimeout(() => {
                if (!isMounted.current) return;
                setFormData(prev => ({
                  ...prev,
                  pickupPhone: bestPhone.phoneNumber,
                }));
                logger.info(
                  `[CaseFormPage] 引取先電話番号を自動設定: ${bestPhone.phoneNumber} (信頼度: ${Math.round(bestPhone.confidence * 100)}%)`
                );
              }, 0);
            } else {
              logger.info(`[CaseFormPage] 電話番号が見つかりませんでした`);
            }
          } catch (error) {
            logger.error('[CaseFormPage] 引取先住所自動設定エラー:', error);
          }
        }
      },
    }),
    []
  );

  // FL-67: 搬入先住所用OCRイベントハンドラー
  const deliveryAddressOCREventHandlers: OCREventHandlers = useMemo(
    () => ({
      onProcessingStart: (fileName: string) => {
        logger.info(`[CaseFormPage] 搬入先住所用OCR処理開始: ${fileName}`);
      },
      onProcessingProgress: (fileName: string, progress: number) => {
        logger.info(
          `[CaseFormPage] 搬入先住所用OCR処理進捗: ${fileName} - ${Math.round(progress * 100)}%`
        );
      },
      onProcessingComplete: (result: ImageOCRResult) => {
        logger.info(`[CaseFormPage] 搬入先住所用OCR処理完了: ${result.fileName}`, result);
      },
      onProcessingError: (fileName: string, error: string) => {
        logger.error(`[CaseFormPage] 搬入先住所用OCR処理エラー: ${fileName}`, error);
        const errorMsg = error.includes('初期化')
          ? 'OCRシステムの初期化中です。しばらく待ってから再度お試しください。'
          : `搬入先住所の画像処理でエラーが発生しました: ${error}`;
        alert(`${fileName}\n${errorMsg}`);
      },
      onAddressExtracted: (addresses: ExtractedAddress[]) => {
        logger.info(`[CaseFormPage] 搬入先住所抽出完了: ${addresses.length}件`, addresses);

        // 住所が抽出された場合、最初の住所を自動設定
        if (addresses.length > 0) {
          const firstAddress = addresses[0];
          logger.info(`[CaseFormPage] 搬入先住所を自動設定: ${firstAddress.fullAddress}`);

          try {
            // 住所を自動設定（非同期でsetState）
            setTimeout(() => {
              if (!isMounted.current) return;
              setFormData(prev => ({
                ...prev,
                deliveryAddress: firstAddress.fullAddress,
              }));
            }, 0);

            // 元のテキストから電話番号を抽出して自動設定
            logger.info(`[CaseFormPage] 電話番号抽出開始 - 元テキスト: "${firstAddress.rawText}"`);
            const phoneNumbers = extractPhoneNumbersFromMultipleTexts([firstAddress.rawText]);
            logger.info(
              `[CaseFormPage] 抽出された電話番号: ${phoneNumbers.length}件`,
              phoneNumbers
            );

            if (phoneNumbers.length > 0) {
              const bestPhone = phoneNumbers[0]; // 最も信頼度の高い電話番号
              setTimeout(() => {
                if (!isMounted.current) return;
                setFormData(prev => ({
                  ...prev,
                  deliveryPhone: bestPhone.phoneNumber,
                }));
                logger.info(
                  `[CaseFormPage] 搬入先電話番号を自動設定: ${bestPhone.phoneNumber} (信頼度: ${Math.round(bestPhone.confidence * 100)}%)`
                );
              }, 0);
            } else {
              logger.info(`[CaseFormPage] 電話番号が見つかりませんでした`);
            }
          } catch (error) {
            logger.error('[CaseFormPage] 搬入先住所自動設定エラー:', error);
          }
        }
      },
    }),
    []
  );

  // AI提案のハンドラー関数
  const convertFormDataToCase = (): Case | null => {
    // 必要な情報が不足している場合はnullを返す
    if (!formData.pickupAddress || !formData.vehicleModel || !formData.pickupDate) {
      return null;
    }

    // formDataからCase型に変換（必要な情報のみ）
    return {
      id: caseId || 'temp-id', // 一時的なID
      status: 'provisional' as const,
      assignedLandTransporterId: formData.assignedLandTransporterId,

      // BusinessPartner IDs (BusinessPartnerSelectionから取得)
      billingPartnerId: businessPartnerSelections.billing?.partnerId || '',
      billingLocationId: businessPartnerSelections.billing?.locationId,
      pickupPartnerId: businessPartnerSelections.pickup?.partnerId || '',
      pickupLocationId: businessPartnerSelections.pickup?.locationId,
      deliveryPartnerId: businessPartnerSelections.delivery?.partnerId || '',
      deliveryLocationId: businessPartnerSelections.delivery?.locationId,

      // Legacy Client IDs
      billingClientId: formData.billingClientId,
      pickupClientId: formData.pickupClientId,
      deliveryClientId: formData.deliveryClientId,

      // Pickup details
      pickupAddress: formData.pickupAddress,
      pickupPhone: formData.pickupPhone,
      pickupDate: formData.pickupDate,
      pickupTimeStart: formData.pickupTimeStart,
      pickupTimeEnd: formData.pickupTimeEnd,
      pickupReport: null,

      // Delivery details
      deliveryAddress: formData.deliveryAddress,
      deliveryPhone: formData.deliveryPhone,
      deliveryDate: formData.deliveryDate,
      deliveryTime: formData.deliveryTime,
      deliveryReport: null,

      // Vehicle details
      vehicleModel: formData.vehicleModel,
      vehicleType: formData.vehicleType,
      vehiclePlateNumber: formData.vehiclePlateNumber,

      // Billing
      price: formData.price,

      // Other
      remarks: formData.remarks,
    };
  };

  const handleGetAISuggestions = async () => {
    // バリデーション: 必要な情報の確認
    if (!formData.pickupAddress) {
      setSuggestionError('AI提案には引取場所の入力が必要です。');
      setShowSuggestions(true);
      return;
    }

    if (!formData.vehicleModel) {
      setSuggestionError('AI提案には車両モデルの選択が必要です。');
      setShowSuggestions(true);
      return;
    }

    if (!formData.pickupDate) {
      setSuggestionError('AI提案には引取日の入力が必要です。');
      setShowSuggestions(true);
      return;
    }

    // formDataをCase型に変換
    const targetCase = convertFormDataToCase();
    if (!targetCase) {
      setSuggestionError('案件情報の準備に失敗しました。');
      setShowSuggestions(true);
      return;
    }

    // 利用可能な陸送員をフィルタ
    const availableTransporters = landTransporters.filter(t => t.status === 'Available');
    if (availableTransporters.length === 0) {
      setSuggestionError('現在利用可能な陸送員がいません。');
      setShowSuggestions(true);
      return;
    }

    setIsGeneratingSuggestions(true);
    setSuggestionError('');

    try {
      const suggestions = await getSuggestedLandTransporters(targetCase, availableTransporters);
      setSuggestedTransporters(suggestions);
      setShowSuggestions(true);
    } catch (err) {
      setSuggestionError(err instanceof Error ? err.message : 'AI提案の取得に失敗しました。');
      setShowSuggestions(true);
      logger.error('AI陸送員提案エラー:', err);
    } finally {
      setIsGeneratingSuggestions(false);
    }
  };

  const handleSelectSuggestedTransporter = (transporterId: string) => {
    // formDataのassignedLandTransporterIdを更新
    setFormData(prev => ({
      ...prev,
      assignedLandTransporterId: transporterId,
    }));

    // 提案表示を非表示
    setShowSuggestions(false);
    setSuggestedTransporters([]);
    setSuggestionError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasInteracted(true);

    // FL-28: BusinessPartnerまたはClient情報の必須チェック（自由入力も含む）
    const hasBilling = businessPartnerSelections.billing || formData.billingClientId;
    const hasPickup =
      businessPartnerSelections.pickup ||
      formData.pickupClientId ||
      formData.customPickupPartnerName;
    const hasDelivery =
      businessPartnerSelections.delivery ||
      formData.deliveryClientId ||
      formData.customDeliveryPartnerName;

    // OCR選択状態のチェック
    if (businessPartnerSelections.pickup?.partnerId === 'ocr-reader') {
      alert(
        '引取先でOCR読み取りを選択中です。読み取り結果から取引先を選択するか、既存の取引先を選択してください。'
      );
      return;
    }

    if (businessPartnerSelections.delivery?.partnerId === 'ocr-reader') {
      alert(
        '搬入先でOCR読み取りを選択中です。読み取り結果から搬入先を選択するか、既存の搬入先を選択してください。'
      );
      return;
    }

    if (!hasBilling || !hasPickup || !hasDelivery) {
      alert('取引先情報（請求先・引取先・搬入先）をすべて選択または入力してください。');
      return;
    }

    if (
      !formData.pickupAddress ||
      !formData.pickupPhone ||
      !formData.pickupDate ||
      !formData.pickupTimeStart ||
      !formData.pickupTimeEnd
    ) {
      alert('引取情報をすべて入力してください。');
      return;
    }

    if (
      !formData.deliveryAddress ||
      !formData.deliveryPhone ||
      !formData.deliveryDate ||
      !formData.deliveryTime
    ) {
      alert('搬入情報をすべて入力してください。');
      return;
    }

    // 車両データのバリデーション
    const vehicleValidation = validateVehicleData(
      formData.vehicleModelId || '',
      formData.vehiclePlateNumber,
      formData.vehiclePlateNumberNone
    );

    if (!vehicleValidation.isValid) {
      alert(vehicleValidation.error);
      return;
    }

    // FL-163: 本受(confirmed)の場合は車体番号必須
    if (formData.status === 'confirmed' && !formData.chassisNumber?.trim()) {
      alert('本受の場合、車体番号の入力は必須です。');
      return;
    }

    setIsLoading(true);
    try {
      // 🔍 デバッグログ: 陸送員割り当ての詳細追跡
      logger.info(`[CaseFormPage] handleSubmit実行 - isEditMode=${isEditMode}`);
      logger.debug(`📋 formData.assignedLandTransporterId の値:`, {
        value: formData.assignedLandTransporterId,
        type: typeof formData.assignedLandTransporterId,
        isEmpty: !formData.assignedLandTransporterId,
      });

      // 🔧 FIX: 空文字列をnullに変換する処理を改善
      // formData.assignedLandTransporterIdは初期値が''（空文字列）
      // ''の場合は未割り当てを意味するため、nullに変換
      // 文字列値が入っている場合はそのまま使用
      const normalizedTransporterId =
        formData.assignedLandTransporterId === ''
          ? null
          : formData.assignedLandTransporterId || undefined;

      // 🔍 陸送員正規化処理のログ
      logger.info(`[TRANSPORTER-DEBUG] 🔄 正規化処理`, {
        original: formData.assignedLandTransporterId,
        originalType: typeof formData.assignedLandTransporterId,
        isEmpty: formData.assignedLandTransporterId === '',
        normalized: normalizedTransporterId,
        normalizedType: typeof normalizedTransporterId,
        isNull: normalizedTransporterId === null,
        isUndefined: normalizedTransporterId === undefined,
      });

      const caseData = {
        id: isEditMode ? caseId! : crypto.randomUUID(),
        status: isEditMode
          ? getCaseById(caseId!)?.status || 'provisional'
          : ('provisional' as const),
        assignedLandTransporterId: isEditMode
          ? normalizedTransporterId // 空文字列→null、値あり→値、undefinedはそのまま
          : undefined, // 新規作成時は常にundefined

        // FL-28: BusinessPartner情報を含める
        // FL-89: 自由入力モード対応 - partner IDがない場合はundefinedを設定
        billingPartnerId: businessPartnerSelections.billing?.partnerId || undefined,
        billingLocationId: businessPartnerSelections.billing?.locationId,
        pickupPartnerId: businessPartnerSelections.pickup?.partnerId || undefined,
        pickupLocationId: businessPartnerSelections.pickup?.locationId,
        deliveryPartnerId: businessPartnerSelections.delivery?.partnerId || undefined,
        deliveryLocationId: businessPartnerSelections.delivery?.locationId,

        // Legacy Client IDs (後方互換性)
        billingClientId: formData.billingClientId,
        pickupClientId: formData.pickupClientId,
        deliveryClientId: formData.deliveryClientId,

        // Pickup details
        pickupAddress: formData.pickupAddress,
        pickupPhone: formData.pickupPhone,
        pickupDate: formData.pickupDate,
        pickupTimeStart: formData.pickupTimeStart,
        pickupTimeEnd: formData.pickupTimeEnd,
        pickupReport: null,
        // FL-89: 引取日期間対応
        pickupDateEnd: formData.pickupDateEnd,

        // Delivery details
        deliveryAddress: formData.deliveryAddress,
        deliveryPhone: formData.deliveryPhone,
        deliveryDate: formData.deliveryDate,
        deliveryTime: formData.deliveryTime,
        // FL-89: 搬入日期間対応
        deliveryDateEnd: formData.deliveryDateEnd,
        deliveryReport: null,

        // Vehicle details
        // Legacy migration: vehicleModel (string) は後方互換性のため保持
        // 新規実装では vehicleModelId (UUID) を使用してください
        vehicleModel: formData.vehicleModel,
        vehicleModelId: formData.vehicleModelId,
        vehicleType: formData.vehicleType,
        vehiclePlateNumber: formData.vehiclePlateNumber,
        // FL-89: 車両関連新規フィールド
        vehiclePlateNumberNone: formData.vehiclePlateNumberNone,

        // FL-89: 住所自由入力フィールド
        // 自由入力モードの場合でも、住所をcustomフィールドに格納（取引先名は格納しない）
        billingAddressCustom: formData.billingAddressCustom,
        pickupAddressCustom: formData.pickupAddress,
        deliveryAddressCustom: formData.deliveryAddress,

        // FL-89: 新規フィールド
        requester: formData.requester,
        managementNumber: formData.managementNumber,
        transportFee: formData.transportFee,

        // Billing
        price: formData.price || 0,

        // FL-163: 車体番号 (acceptanceStatus removed - now using status)
        chassisNumber: formData.chassisNumber,

        // Other
        remarks: formData.remarks,
      };

      // 🔍 caseData構築結果の完全ログ
      logger.info(`[TRANSPORTER-DEBUG] 📦 caseData構築`, {
        isEditMode,
        caseId,
        assignedLandTransporterId: caseData.assignedLandTransporterId,
        normalizedTransporterId,
        willUseNormalized: isEditMode,
        willUseUndefined: !isEditMode,
      });

      // 🔍 updateCase/addCase呼び出しの判定ログ
      logger.info(`[TRANSPORTER-DEBUG] 💾 保存直前データ`, {
        methodToCall: isEditMode && caseId ? 'updateCase' : 'addCase',
        caseId: isEditMode ? caseId : 'new',
        assignedLandTransporterId: caseData.assignedLandTransporterId,
        type: typeof caseData.assignedLandTransporterId,
        isNull: caseData.assignedLandTransporterId === null,
        isUndefined: caseData.assignedLandTransporterId === undefined,
      });

      if (isEditMode && caseId) {
        logger.info(`[TRANSPORTER-DEBUG] 🚀 updateCase実行`, {
          caseId,
          assignedLandTransporterId: caseData.assignedLandTransporterId,
        });
        await updateCase(caseId, caseData);
      } else {
        logger.info(`[TRANSPORTER-DEBUG] 🚀 addCase実行`, {
          assignedLandTransporterId: caseData.assignedLandTransporterId,
        });
        await addCase(caseData);
      }

      navigate('/cases');
    } catch (error) {
      logger.error('案件の保存に失敗しました:', error);
      alert('保存に失敗しました。もう一度お試しください。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // FL-163: スケジュール画面から遷移してきた場合はスケジュール画面に戻る
    if (queryTransporterId || queryPickupDate || queryPickupTimeStart) {
      navigate('/schedule');
    } else {
      navigate('/cases');
    }
  };

  return (
    <div className='max-w-4xl mx-auto px-4 sm:px-0'>
      <PageTitle icon={DocumentPlusIcon} title={isEditMode ? '案件編集' : '新規案件を登録する'} />

      <div className='flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0'>
        <div className='flex items-center'>
          <Button
            onClick={handleCancel}
            variant='ghost'
            size='md'
            className='mr-3 sm:mr-4 min-h-[44px] min-w-[44px] p-2'
            aria-label='キャンセル'
          >
            <ArrowLeftIcon className='h-5 w-5 sm:h-6 sm:w-6' />
          </Button>
        </div>
      </div>

      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-8'>
        <form onSubmit={handleSubmit} className='space-y-6 sm:space-y-8'>
          {/* 案件日（DateContext 連動） */}
          <div className='max-w-md'>
            <label
              htmlFor='caseDate'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
            >
              案件日
            </label>
            <input
              type='date'
              id='caseDate'
              name='caseDate'
              aria-label='案件日'
              value={selectedDate}
              onChange={e => {
                const newDate = e.target.value;
                if (newDate) {
                  // DateContextのselectedDateを更新（新規作成時は引取日/搬入日も同期）
                  setSelectedDate(newDate);
                }
              }}
              disabled={isLoading}
              className='w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 min-h-[44px] touch-target-input'
            />
          </div>

          {/* FL-232: 受付状態選択フィールド */}
          <div className='max-w-md space-y-2'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
              受付状態
            </label>

            {/* トグルボタングループ */}
            <div className='inline-flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1'>
              <Button
                type='button'
                variant={
                  formData.status === 'provisional' || !formData.status ? 'primary' : 'ghost'
                }
                size='sm'
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    status: 'provisional',
                  }));
                }}
                disabled={isLoading}
                aria-pressed={formData.status === 'provisional' || !formData.status}
                aria-label='仮受を選択'
                className='px-4 py-2'
              >
                仮受
              </Button>
              <Button
                type='button'
                variant={formData.status === 'confirmed' ? 'primary' : 'ghost'}
                size='sm'
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    status: 'confirmed',
                  }));
                }}
                disabled={isLoading}
                aria-pressed={formData.status === 'confirmed'}
                aria-label='本受を選択'
                className='px-4 py-2'
              >
                本受
              </Button>
            </div>

            {/* 説明文 */}
            <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
              {formData.status === 'confirmed'
                ? '正式依頼書受領後、車体番号入力済み'
                : '電話・LINE相談段階、車体番号未確定'}
            </p>
          </div>

          {/* FL-28: 取引先情報セクション */}
          <div>
            <h2 className='text-lg sm:text-xl font-semibold text-gray-800 dark:text-white mb-3 sm:mb-4 flex items-center'>
              <TruckIcon className='h-5 w-5 sm:h-6 sm:w-6 mr-2' />
              取引先情報
            </h2>
            {/* デバッグログ: BusinessPartnerFormへ渡される値 */}
            {(() => {
              logger.info('[CaseFormPage] BusinessPartnerForm props:', {
                billing: businessPartnerSelections.billing,
                billingClientId: formData.billingClientId,
                isLoading,
              });
              return null;
            })()}
            <BusinessPartnerForm
              billing={businessPartnerSelections.billing}
              onBillingChange={selection => handleBusinessPartnerChange('billing', selection)}
              disabled={isLoading}
              currentBillingPartnerId={formData.billingPartnerId}
            />
          </div>

          {/* 引取情報セクション */}
          <div>
            <h2 className='text-lg sm:text-xl font-semibold text-gray-800 dark:text-white mb-3 sm:mb-4'>
              引取情報
            </h2>
            <div className='space-y-4 sm:space-y-6'>
              {/* 引取先選択 */}
              <div className='max-w-md'>
                <BusinessPartnerSelector
                  value={businessPartnerSelections.pickup}
                  onChange={selection => handleBusinessPartnerChange('pickup', selection)}
                  role={BusinessPartnerRole.PICKUP}
                  label='引取先'
                  placeholder='引取先を選択してください'
                  required={true}
                  disabled={isLoading}
                  currentPartnerId={formData.pickupPartnerId}
                  allowCustomInput={true}
                />
              </div>

              {/* FL-110: 締切ルール適用通知 - 引取先 */}
              {(appliedDeadlineRules.pickup || deadlineRuleLoading.pickup) && (
                <Alert variant='info' className='mt-4'>
                  <div className='flex items-start'>
                    <div className='ml-3'>
                      {appliedDeadlineRules.pickup ? (
                        <>
                          <h4 className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                            引取締切ルール適用済み{deadlineRuleLoading.pickup ? ' (更新中...)' : ''}
                          </h4>
                          <p className='text-sm text-blue-700 dark:text-blue-300 mt-1'>
                            {appliedDeadlineRules.pickup.partnerName}
                            の締切ルールが適用され、引取開始時間を
                            {appliedDeadlineRules.pickup.rule.deadlineTime}に自動設定しました
                          </p>
                          <p className='text-xs text-blue-600 dark:text-blue-400 mt-1'>
                            対象曜日:{' '}
                            {
                              [
                                '日曜日',
                                '月曜日',
                                '火曜日',
                                '水曜日',
                                '木曜日',
                                '金曜日',
                                '土曜日',
                              ][appliedDeadlineRules.pickup.dayOfWeek]
                            }
                          </p>
                        </>
                      ) : deadlineRuleLoading.pickup ? (
                        <>
                          <h4 className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                            締切ルールを確認しています...
                          </h4>
                          <p className='text-sm text-blue-700 dark:text-blue-300 mt-1'>
                            選択された引取先の締切ルールを適用中です
                          </p>
                        </>
                      ) : null}
                    </div>
                  </div>
                </Alert>
              )}

              {/* FL-67: 引取先OCR表示 */}
              {businessPartnerSelections.pickup?.partnerId === 'ocr-reader' && (
                <div className='bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4'>
                  <div className='flex items-center mb-3'>
                    <PhotoIcon className='h-5 w-5 text-green-600 mr-2' />
                    <h4 className='font-medium text-green-800 dark:text-green-200'>
                      画像から引取先情報を読み取り
                    </h4>
                  </div>
                  <p className='text-sm text-green-700 dark:text-green-300 mb-4'>
                    引取先の住所や連絡先が記載された書類や名刺の画像をアップロードしてください。
                  </p>
                  {ocrSettings?.ocrEnabled ? (
                    <ImageUpload
                      config={{
                        ...DEFAULT_IMAGE_UPLOAD_CONFIG,
                        maxFiles:
                          ocrSettings?.maxFilesPerUpload || DEFAULT_IMAGE_UPLOAD_CONFIG.maxFiles,
                        maxSizeBytes: (ocrSettings?.maxFileSizeMB || 5) * 1024 * 1024,
                        extractionOptions: {
                          ...DEFAULT_IMAGE_UPLOAD_CONFIG.extractionOptions,
                          confidenceThreshold: ocrSettings?.confidenceThreshold || 0.7,
                        },
                      }}
                      eventHandlers={pickupAddressOCREventHandlers}
                      onResultsChange={handlePickupAddressOCRResultsChange}
                      disabled={isLoading}
                      className='mb-4'
                    />
                  ) : (
                    <div className='p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center'>
                      <PhotoIcon className='h-8 w-8 text-gray-400 mx-auto mb-2' />
                      <p className='text-sm text-gray-500'>
                        OCR機能が無効になっています。システム設定で有効にしてください。
                      </p>
                    </div>
                  )}
                  {/* 引取先住所選択 */}
                  {showPickupAddressSelection && (
                    <AddressSelectionUI
                      addresses={pickupAddressAddresses}
                      onAddressSelect={handlePickupAddressSelect}
                      onClose={() => setShowPickupAddressSelection(false)}
                      title='抽出された引取先情報'
                      actionButtonText='住所・電話番号を使用'
                      colorScheme='green'
                    />
                  )}
                </div>
              )}

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6'>
                {/* disabled制御: 引取先が取引先選択モードの場合はdisabled */}
                {(() => {
                  const isPickupAddressDisabled =
                    isLoading || businessPartnerSelections.pickup?.partnerId != null;
                  return (
                    <div>
                      <AddressAutocompleteInput
                        id='pickupAddress'
                        label='引取住所'
                        placeholder='東京都千代田区丸の内1-1-1'
                        value={formData.pickupAddress}
                        onChange={value => {
                          setFormData(prev => ({
                            ...prev,
                            pickupAddress: value,
                          }));
                        }}
                        onAddressSelect={handlePickupAddressAutocompleteSelect}
                        required
                        disabled={isPickupAddressDisabled}
                        className='w-full'
                        aria-label='引取住所を入力してください'
                      />
                    </div>
                  );
                })()}

                <div>
                  <label
                    htmlFor='pickupPhone'
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
                  >
                    引取連絡先 *
                  </label>
                  <input
                    type='text'
                    name='pickupPhone'
                    id='pickupPhone'
                    value={formData.pickupPhone}
                    onChange={handleInputChange}
                    placeholder='03-1234-5678'
                    required
                    disabled={isLoading}
                    data-testid='pickup-phone-input'
                    className='w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 min-h-[44px] touch-target-input'
                  />
                </div>

                <div>
                  <label
                    htmlFor='pickupDate'
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
                  >
                    引取日 *
                  </label>
                  <input
                    type='date'
                    name='pickupDate'
                    id='pickupDate'
                    aria-label='引取日'
                    value={formData.pickupDate}
                    onChange={handleInputChange}
                    required
                    disabled={isLoading}
                    className='w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 min-h-[44px] touch-target-input'
                  />
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-3'>
                  <TimeSelector
                    label='引取開始時間'
                    value={formData.pickupTimeStart}
                    onChange={value => {
                      setHasInteracted(true); // ユーザー操作を記録
                      setFormData(prev => {
                        const next = { ...prev, pickupTimeStart: value } as typeof prev;
                        // 引取開始時間と終了時間の自動調整
                        if (value && prev.pickupTimeEnd) {
                          const [startHour, startMinute] = value.split(':').map(Number);
                          const [endHour, endMinute] = prev.pickupTimeEnd.split(':').map(Number);

                          if (
                            !isNaN(startHour) &&
                            !isNaN(startMinute) &&
                            !isNaN(endHour) &&
                            !isNaN(endMinute)
                          ) {
                            const startTotalMinutes = startHour * 60 + startMinute;
                            const endTotalMinutes = endHour * 60 + endMinute;

                            // 新規追加: 開始時間が終了時間以降の場合、バッファ時間を加えて終了時間を調整
                            if (endTotalMinutes < startTotalMinutes) {
                              const bufferMinutes =
                                companyTimeSettings?.bufferTimeSettings?.betweenCasesBuffer ?? 30;
                              const newEndTotalMinutes =
                                (startTotalMinutes + bufferMinutes) % (24 * 60);
                              const newEndHour = Math.floor(newEndTotalMinutes / 60);
                              const newEndMinute = newEndTotalMinutes % 60;
                              const newEndTime = `${newEndHour.toString().padStart(2, '0')}:${newEndMinute
                                .toString()
                                .padStart(2, '0')}`;
                              next.pickupTimeEnd = newEndTime;
                              logger.info(
                                '[TimeSelector] 開始時間が終了時間を超えたため、バッファ時間を加えて終了時間を自動調整:',
                                {
                                  startTime: value,
                                  oldEndTime: prev.pickupTimeEnd,
                                  newEndTime,
                                  bufferMinutes,
                                }
                              );
                            } else {
                              // 既存のバッファ時間調整ロジック
                              const bufferMinutes =
                                companyTimeSettings?.bufferTimeSettings?.betweenCasesBuffer ?? 30;
                              let adjustedEndTotalMinutes = endTotalMinutes;
                              if (adjustedEndTotalMinutes <= startTotalMinutes) {
                                adjustedEndTotalMinutes += 24 * 60;
                              }
                              const workDuration = adjustedEndTotalMinutes - startTotalMinutes;
                              if (pickupEndWasInsufficient || workDuration < bufferMinutes) {
                                const newEndTotalMinutes =
                                  (startTotalMinutes + bufferMinutes) % (24 * 60);
                                const newEndHour = Math.floor(newEndTotalMinutes / 60);
                                const newEndMinute = newEndTotalMinutes % 60;
                                const newEndTime = `${newEndHour.toString().padStart(2, '0')}:${newEndMinute
                                  .toString()
                                  .padStart(2, '0')}`;
                                logger.info(
                                  '[TimeSelector] バッファ時間確保のため終了時間を自動調整:',
                                  {
                                    startTime: value,
                                    oldEndTime: prev.pickupTimeEnd,
                                    newEndTime,
                                    bufferMinutes,
                                    workDuration,
                                  }
                                );
                                next.pickupTimeEnd = newEndTime;
                                setPickupEndWasInsufficient(false);
                              }
                            }
                          }
                        }
                        return next;
                      });
                    }}
                    required
                    disabled={isLoading}
                    id='pickupTimeStart'
                    name='pickupTimeStart'
                    timeSettings={
                      companyTimeSettings?.pickupTimeSettings || DEFAULT_PICKUP_TIME_SETTINGS
                    }
                    intervalMinutes={10}
                    error={timeValidationErrors.pickupTimeStart}
                  />
                  <TimeSelector
                    label='引取終了時間'
                    value={formData.pickupTimeEnd}
                    onChange={value => {
                      setHasInteracted(true); // ユーザー操作を記録
                      setFormData(prev => ({ ...prev, pickupTimeEnd: value }));
                      pickupEndRef.current = value || '';
                      // バッファ不足状態を更新
                      if (formData.pickupTimeStart && value) {
                        const bufferMinutes =
                          companyTimeSettings?.bufferTimeSettings?.betweenCasesBuffer ?? 30;
                        const [startHour, startMinute] = formData.pickupTimeStart
                          .split(':')
                          .map(Number);
                        const [endHour, endMinute] = value.split(':').map(Number);
                        if (
                          !isNaN(startHour) &&
                          !isNaN(startMinute) &&
                          !isNaN(endHour) &&
                          !isNaN(endMinute)
                        ) {
                          const startTotalMinutes = startHour * 60 + startMinute;
                          let endTotalMinutes = endHour * 60 + endMinute;
                          if (endTotalMinutes <= startTotalMinutes) {
                            endTotalMinutes += 24 * 60;
                          }
                          const workDuration = endTotalMinutes - startTotalMinutes;
                          setPickupEndWasInsufficient(workDuration < bufferMinutes);
                        }
                      }
                    }}
                    required
                    disabled={isLoading}
                    id='pickupTimeEnd'
                    name='pickupTimeEnd'
                    timeSettings={pickupEndTimeSettings}
                    intervalMinutes={10}
                    error={timeValidationErrors.pickupTimeEnd}
                  />
                </div>
              </div>

              {/* 引取締切警告 */}
              {deadlineWarnings.pickup && (
                <Alert variant='warning' className='mt-4'>
                  <div className='flex items-start'>
                    <div className='ml-3'>
                      <h4 className='text-sm font-medium text-yellow-800 dark:text-yellow-200'>
                        引取締切違反警告
                      </h4>
                      <p className='text-sm text-yellow-700 dark:text-yellow-300 mt-1'>
                        {deadlineWarnings.pickup.warningMessage}
                      </p>
                      <p className='text-xs text-yellow-600 dark:text-yellow-400 mt-1'>
                        締切時間: {deadlineWarnings.pickup.applicableRule?.deadlineTime} (
                        {deadlineWarnings.pickup.dayOfWeekName})
                      </p>
                    </div>
                  </div>
                </Alert>
              )}
            </div>
          </div>

          {/* 搬入情報セクション */}
          <div>
            <h2 className='text-lg sm:text-xl font-semibold text-gray-800 dark:text-white mb-3 sm:mb-4'>
              搬入情報
            </h2>
            <div className='space-y-4 sm:space-y-6'>
              {/* 搬入先選択 */}
              <div className='max-w-md'>
                <BusinessPartnerSelector
                  value={businessPartnerSelections.delivery}
                  onChange={selection => handleBusinessPartnerChange('delivery', selection)}
                  role={BusinessPartnerRole.DELIVERY}
                  label='搬入先'
                  placeholder='搬入先を選択してください'
                  required={true}
                  disabled={isLoading}
                  currentPartnerId={formData.deliveryPartnerId}
                  allowCustomInput={true}
                />
              </div>

              {/* FL-110: 締切ルール適用通知 - 搬入先 */}
              {(appliedDeadlineRules.delivery || deadlineRuleLoading.delivery) && (
                <Alert variant='info' className='mt-4'>
                  <div className='flex items-start'>
                    <div className='ml-3'>
                      {appliedDeadlineRules.delivery ? (
                        <>
                          <h4 className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                            搬入締切ルール適用済み
                            {deadlineRuleLoading.delivery ? ' (更新中...)' : ''}
                          </h4>
                          <p className='text-sm text-blue-700 dark:text-blue-300 mt-1'>
                            {appliedDeadlineRules.delivery.partnerName}
                            の締切ルールが適用され、搬入時間を
                            {appliedDeadlineRules.delivery.rule.deadlineTime}に自動設定しました
                          </p>
                          <p className='text-xs text-blue-600 dark:text-blue-400 mt-1'>
                            対象曜日:{' '}
                            {
                              [
                                '日曜日',
                                '月曜日',
                                '火曜日',
                                '水曜日',
                                '木曜日',
                                '金曜日',
                                '土曜日',
                              ][appliedDeadlineRules.delivery.dayOfWeek]
                            }
                          </p>
                        </>
                      ) : deadlineRuleLoading.delivery ? (
                        <>
                          <h4 className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                            締切ルールを確認しています...
                          </h4>
                          <p className='text-sm text-blue-700 dark:text-blue-300 mt-1'>
                            選択された搬入先の締切ルールを適用中です
                          </p>
                        </>
                      ) : null}
                    </div>
                  </div>
                </Alert>
              )}

              {/* FL-67: 搬入先OCR表示 */}
              {businessPartnerSelections.delivery?.partnerId === 'ocr-reader' && (
                <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
                  <div className='flex items-center mb-3'>
                    <PhotoIcon className='h-5 w-5 text-blue-600 mr-2' />
                    <h4 className='font-medium text-blue-800 dark:text-blue-200'>
                      画像から搬入先情報を読み取り
                    </h4>
                  </div>
                  <p className='text-sm text-blue-700 dark:text-blue-300 mb-4'>
                    搬入先の住所や連絡先が記載された書類や名刺の画像をアップロードしてください。
                  </p>
                  {ocrSettings?.ocrEnabled ? (
                    <ImageUpload
                      config={{
                        ...DEFAULT_IMAGE_UPLOAD_CONFIG,
                        maxFiles:
                          ocrSettings?.maxFilesPerUpload || DEFAULT_IMAGE_UPLOAD_CONFIG.maxFiles,
                        maxSizeBytes: (ocrSettings?.maxFileSizeMB || 5) * 1024 * 1024,
                        extractionOptions: {
                          ...DEFAULT_IMAGE_UPLOAD_CONFIG.extractionOptions,
                          confidenceThreshold: ocrSettings?.confidenceThreshold || 0.7,
                        },
                      }}
                      eventHandlers={deliveryAddressOCREventHandlers}
                      onResultsChange={handleDeliveryAddressOCRResultsChange}
                      disabled={isLoading}
                      className='mb-4'
                    />
                  ) : (
                    <div className='p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center'>
                      <PhotoIcon className='h-8 w-8 text-gray-400 mx-auto mb-2' />
                      <p className='text-sm text-gray-500'>
                        OCR機能が無効になっています。システム設定で有効にしてください。
                      </p>
                    </div>
                  )}
                  {/* 搬入先住所選択 */}
                  {showDeliveryAddressSelection && (
                    <AddressSelectionUI
                      addresses={deliveryAddressAddresses}
                      onAddressSelect={handleDeliveryAddressSelect}
                      onClose={() => setShowDeliveryAddressSelection(false)}
                      title='抽出された搬入先情報'
                      actionButtonText='住所・電話番号を使用'
                      colorScheme='blue'
                    />
                  )}
                </div>
              )}

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6'>
                {/* disabled制御: 搬入先が取引先選択モードの場合はdisabled */}
                {(() => {
                  const isDeliveryAddressDisabled =
                    isLoading || businessPartnerSelections.delivery?.partnerId != null;
                  return (
                    <div>
                      <AddressAutocompleteInput
                        id='deliveryAddress'
                        label='搬入住所'
                        placeholder='愛知県名古屋市中村区名駅4-7-1'
                        value={formData.deliveryAddress}
                        onChange={value => {
                          setFormData(prev => ({
                            ...prev,
                            deliveryAddress: value,
                          }));
                        }}
                        onAddressSelect={handleDeliveryAddressAutocompleteSelect}
                        required
                        disabled={isDeliveryAddressDisabled}
                        className='w-full'
                      />
                    </div>
                  );
                })()}

                <div>
                  <label
                    htmlFor='deliveryPhone'
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
                  >
                    搬入連絡先 *
                  </label>
                  <input
                    type='text'
                    name='deliveryPhone'
                    id='deliveryPhone'
                    value={formData.deliveryPhone}
                    onChange={handleInputChange}
                    placeholder='052-1111-2222'
                    required
                    disabled={isLoading}
                    data-testid='delivery-phone-input'
                    className='w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 min-h-[44px] touch-target-input'
                  />
                </div>

                <div>
                  <label
                    htmlFor='deliveryDate'
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
                  >
                    搬入日 *
                  </label>
                  <input
                    type='date'
                    name='deliveryDate'
                    id='deliveryDate'
                    aria-label='搬入日'
                    value={formData.deliveryDate}
                    onChange={handleInputChange}
                    required
                    disabled={isLoading}
                    className='w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 min-h-[44px] touch-target-input'
                  />
                </div>

                <div>
                  <TimeSelector
                    label='搬入時間'
                    value={formData.deliveryTime}
                    onChange={value => {
                      setHasInteracted(true);
                      setFormData(prev => ({ ...prev, deliveryTime: value }));
                    }}
                    required
                    disabled={isLoading}
                    id='deliveryTime'
                    name='deliveryTime'
                    timeSettings={
                      companyTimeSettings?.deliveryTimeSettings || DEFAULT_DELIVERY_TIME_SETTINGS
                    }
                    intervalMinutes={10}
                    error={timeValidationErrors.deliveryTime}
                  />
                </div>
              </div>

              {/* FL-170: Google Maps Directions API によるルート可視化 */}
              {routeVisualizationData && (
                <div className='col-span-full'>
                  <RouteVisualizationComponent
                    routeData={routeVisualizationData}
                    showDetails={true}
                    mapHeight={400}
                    isLoading={false}
                    onRefresh={async () => {
                      if (
                        !formData.pickupAddress ||
                        !formData.deliveryAddress ||
                        !isMounted.current
                      ) {
                        return;
                      }
                      try {
                        const result = await distanceCalculationService.calculate(
                          formData.pickupAddress,
                          formData.deliveryAddress,
                          { forceRefresh: true }
                        );
                        if (result && isMounted.current) {
                          setFormData(prev => ({
                            ...prev,
                            distance_km: Number((result.distance / 1000).toFixed(1)),
                            estimated_duration_minutes: Number((result.duration / 60).toFixed(1)),
                            distance_calculated_at: result.cachedAt,
                          }));
                          setRouteVisualizationData({
                            pickup_address: formData.pickupAddress,
                            delivery_address: formData.deliveryAddress,
                            distance_km: Number((result.distance / 1000).toFixed(1)),
                            estimated_duration_minutes: Number((result.duration / 60).toFixed(1)),
                            calculated_at: result.cachedAt,
                            distance_source: result.source,
                            polyline_points: [] as Array<{ lat: number; lng: number }>,
                            steps: [],
                          });
                        }
                      } catch (error) {
                        console.warn('ルート再計算エラー:', error);
                      }
                    }}
                    isRefreshing={false}
                  />
                </div>
              )}

              {/* 搬入締切警告 */}
              {deadlineWarnings.delivery && (
                <div className='col-span-full'>
                  <Alert variant='warning' className='mt-4'>
                    <div className='flex items-start'>
                      <div className='ml-3'>
                        <h4 className='text-sm font-medium text-yellow-800 dark:text-yellow-200'>
                          搬入締切違反警告
                        </h4>
                        <p className='text-sm text-yellow-700 dark:text-yellow-300 mt-1'>
                          {deadlineWarnings.delivery.warningMessage}
                        </p>
                        <p className='text-xs text-yellow-600 dark:text-yellow-400 mt-1'>
                          締切時間: {deadlineWarnings.delivery.applicableRule?.deadlineTime} (
                          {deadlineWarnings.delivery.dayOfWeekName})
                        </p>
                      </div>
                    </div>
                  </Alert>
                </div>
              )}
            </div>
          </div>

          {/* 車両情報セクション */}
          <div>
            <h2 className='text-lg sm:text-xl font-semibold text-gray-800 dark:text-white mb-3 sm:mb-4'>
              車両情報
            </h2>
            <div className='space-y-4 sm:space-y-6'>
              {/* 車両モデル選択 */}
              <div className='max-w-md'>
                <VehicleModelSelector
                  value={formData.vehicleModelId || ''}
                  onChange={handleVehicleModelChange}
                  label='車両モデル'
                  placeholder='車両モデルを選択してください'
                  required={true}
                  disabled={isLoading}
                />
              </div>

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6'>
                <div>
                  <VehiclePlateNumberValidator
                    value={formData.vehiclePlateNumber}
                    onChange={value =>
                      setFormData(prev => ({ ...prev, vehiclePlateNumber: value }))
                    }
                    companyId={companyId || ''}
                    excludeCaseId={isEditMode ? caseId : undefined}
                    label='ナンバープレート番号'
                    placeholder='引取日が近づくにつれて設定される場合があります'
                    required={false}
                    disabled={isLoading}
                  />
                </div>

                {/* 車体番号（FL-163 受付ステータスボタン削除後は常に表示） */}
                {(formData.status === 'confirmed' || formData.status === 'provisional') && (
                  <div>
                    <label
                      htmlFor='chassisNumber'
                      className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
                    >
                      車体番号 <span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='text'
                      name='chassisNumber'
                      id='chassisNumber'
                      value={formData.chassisNumber || ''}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, chassisNumber: e.target.value }))
                      }
                      placeholder='例: ABC-1234567'
                      disabled={isLoading}
                      required
                      className='w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 disabled:opacity-50'
                    />
                    <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                      車両固有の識別番号（ナンバープレートとは別）
                    </p>
                  </div>
                )}
              </div>

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6'>
                <div>
                  <label
                    htmlFor='vehicleType'
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
                  >
                    車種
                  </label>
                  <input
                    type='text'
                    name='vehicleType'
                    id='vehicleType'
                    value={formData.vehicleType}
                    placeholder='車両モデルを選択すると自動設定されます'
                    disabled
                    className='w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 陸送員割り当てセクション（編集時のみ） */}
          {isEditMode && (
            <div>
              <h2 className='text-lg sm:text-xl font-semibold text-gray-800 dark:text-white mb-3 sm:mb-4 flex items-center'>
                <UserIcon className='h-5 w-5 sm:h-6 sm:w-6 mr-2' />
                陸送員割り当て
              </h2>
              <div>
                <div className='flex items-center justify-between mb-1'>
                  <label
                    htmlFor='assignedLandTransporterId'
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                  >
                    担当陸送員
                  </label>
                  <Button
                    type='button'
                    onClick={handleGetAISuggestions}
                    disabled={isGeneratingSuggestions || isLoading}
                    variant='primary'
                    size='sm'
                    className='inline-flex items-center px-3 py-2 !bg-purple-600 !border-purple-600 dark:!bg-purple-600 dark:!border-purple-600 hover:!bg-purple-700 dark:hover:!bg-purple-700 text-xs font-medium min-h-[44px]'
                  >
                    <SparklesIcon className='h-3 w-3 mr-1' />
                    {isGeneratingSuggestions ? 'AI提案中...' : 'AI提案'}
                  </Button>
                </div>
                <select
                  name='assignedLandTransporterId'
                  id='assignedLandTransporterId'
                  value={formData.assignedLandTransporterId ?? ''}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className='w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 min-h-[44px] touch-target-input'
                >
                  <option value=''>未割り当て</option>
                  {landTransporters
                    .filter(transporter => transporter.status !== 'Unavailable')
                    .sort((a, b) => {
                      // 対応可能な陸送員を上位に表示
                      if (a.status === 'Available' && b.status !== 'Available') return -1;
                      if (a.status !== 'Available' && b.status === 'Available') return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map(transporter => {
                      const statusText =
                        transporter.status === 'Available'
                          ? '対応可能'
                          : transporter.status === 'Busy'
                            ? '稼働中'
                            : '対応不可';
                      return (
                        <option key={transporter.id} value={transporter.id}>
                          {transporter.name} - {statusText}
                        </option>
                      );
                    })}
                </select>

                {/* AI提案結果表示エリア */}
                {showSuggestions && (
                  <div
                    className='mt-3 mb-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3'
                    ref={el => {
                      if (el && showSuggestions) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                      }
                    }}
                  >
                    <div className='flex items-center justify-between mb-2'>
                      <div className='flex items-center'>
                        <SparklesIcon className='h-4 w-4 text-purple-500 mr-1' />
                        <h4 className='text-sm font-medium text-purple-800 dark:text-purple-200'>
                          AI推薦陸送員 ({suggestedTransporters.length}件)
                        </h4>
                      </div>
                      <button
                        type='button'
                        onClick={() => setShowSuggestions(false)}
                        className='text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200'
                      >
                        <XMarkIcon className='h-4 w-4' />
                      </button>
                    </div>

                    {suggestionError ? (
                      <div className='bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700 rounded-md p-2'>
                        <p className='text-sm text-red-800 dark:text-red-200'>{suggestionError}</p>
                        <p className='text-xs text-red-600 dark:text-red-400 mt-1'>
                          手動で陸送員を選択してください。
                        </p>
                      </div>
                    ) : (
                      <div className='space-y-2'>
                        {suggestedTransporters.map((suggestion, index) => {
                          const transporter = landTransporters.find(
                            t => t.id === suggestion.transporterId
                          );
                          if (!transporter) return null;

                          return (
                            <div
                              key={index}
                              className='bg-purple-100 dark:bg-purple-900/40 border border-purple-300 dark:border-purple-700 rounded-md p-2'
                            >
                              <div className='flex items-center justify-between'>
                                <div className='flex-1'>
                                  <div className='font-medium text-purple-800 dark:text-purple-200 text-sm'>
                                    {transporter.name} ({transporter.phone})
                                  </div>
                                  <div className='text-xs text-purple-600 dark:text-purple-300 mt-1'>
                                    推薦理由: {suggestion.reason}
                                  </div>
                                </div>
                                <Button
                                  type='button'
                                  onClick={() =>
                                    handleSelectSuggestedTransporter(suggestion.transporterId)
                                  }
                                  variant='primary'
                                  size='sm'
                                  className='px-3 py-2 !bg-purple-600 !border-purple-600 dark:!bg-purple-600 dark:!border-purple-600 hover:!bg-purple-700 dark:hover:!bg-purple-700 text-xs ml-2 min-h-[44px] min-w-[44px]'
                                >
                                  選択
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <p className='text-xs text-purple-700 dark:text-purple-300 mt-2'>
                      AI提案を参考に、最適な陸送員を選択してください。
                    </p>
                  </div>
                )}

                <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                  対応可能・稼働中の陸送員が表示されます（対応不可は除く）
                </p>
              </div>
            </div>
          )}

          {/* FL-89: その他情報セクション拡張 */}
          <div>
            <h2 className='text-lg sm:text-xl font-semibold text-gray-800 dark:text-white mb-3 sm:mb-4'>
              その他情報
            </h2>

            {/* FL-89: 新規フィールド */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6'>
              {/* 依頼者（担当者） */}
              <div>
                <label
                  htmlFor='requester'
                  className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
                >
                  依頼者（担当者）
                </label>
                <input
                  type='text'
                  name='requester'
                  id='requester'
                  value={formData.requester || ''}
                  onChange={handleInputChange}
                  placeholder='依頼者の名前を入力してください'
                  disabled={isLoading}
                  className='w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 min-h-[44px] touch-target-input'
                />
              </div>

              {/* 案件番号（編集時のみ読み取り専用で表示） */}
              {isEditMode &&
                caseId &&
                (() => {
                  const existingCase = getCaseById(caseId);
                  return existingCase?.caseNumber ? (
                    <div>
                      <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                        案件番号
                      </label>
                      <input
                        type='text'
                        value={existingCase.caseNumber}
                        readOnly
                        disabled
                        className='w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed min-h-[44px]'
                      />
                    </div>
                  ) : null;
                })()}

              {/* 管理番号 */}
              <div>
                <label
                  htmlFor='managementNumber'
                  className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
                >
                  管理番号
                </label>
                <input
                  type='text'
                  name='managementNumber'
                  id='managementNumber'
                  value={formData.managementNumber || ''}
                  onChange={handleInputChange}
                  placeholder='社内管理番号を入力してください'
                  disabled={isLoading}
                  className='w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 min-h-[44px] touch-target-input'
                />
              </div>

              {/* 陸送費 - FL-208: 陸送員（DRIVER）には表示しない */}
              {canViewPricing && (
                <div>
                  <label
                    htmlFor='transportFee'
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
                  >
                    陸送費（円）
                  </label>
                  <input
                    type='number'
                    name='transportFee'
                    id='transportFee'
                    value={formData.transportFee || ''}
                    onChange={handleInputChange}
                    placeholder='陸送費を入力してください'
                    disabled={isLoading}
                    min='0'
                    step='500'
                    className='w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 min-h-[44px] touch-target-input'
                  />
                </div>
              )}
            </div>

            {/* FL-113: 車検状態管理 */}
            <div className='border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800'>
              <h3 className='text-sm font-semibold text-gray-900 dark:text-white mb-3'>
                車検状態管理
              </h3>
              <div className='space-y-3'>
                {/* 車検切れチェックボックス */}
                <div className='flex items-start'>
                  <input
                    type='checkbox'
                    id='isVehicleInspectionExpired'
                    name='isVehicleInspectionExpired'
                    checked={formData.isVehicleInspectionExpired || false}
                    onChange={e => {
                      const isExpired = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        isVehicleInspectionExpired: isExpired,
                        // 車検切れでない場合は仮ナンバー関連をリセット
                        requiresTemporaryNumber: isExpired ? prev.requiresTemporaryNumber : false,
                        temporaryNumberNotes: isExpired ? prev.temporaryNumberNotes : '',
                      }));
                    }}
                    disabled={isLoading}
                    className='h-4 w-4 mt-1 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500'
                  />
                  <label
                    htmlFor='isVehicleInspectionExpired'
                    className='ml-2 text-sm text-gray-900 dark:text-gray-300'
                  >
                    <span className='font-medium'>車検切れ</span>
                    <span className='block text-xs text-gray-500 dark:text-gray-400 mt-0.5'>
                      車検の有効期限が切れている車両の場合にチェック
                    </span>
                  </label>
                </div>

                {/* 車検切れの場合のみ表示 */}
                {formData.isVehicleInspectionExpired && (
                  <>
                    {/* 仮ナンバー要否チェックボックス */}
                    <div className='flex items-start ml-6'>
                      <input
                        type='checkbox'
                        id='requiresTemporaryNumber'
                        name='requiresTemporaryNumber'
                        checked={formData.requiresTemporaryNumber || false}
                        onChange={e => {
                          setFormData(prev => ({
                            ...prev,
                            requiresTemporaryNumber: e.target.checked,
                            temporaryNumberNotes: e.target.checked ? prev.temporaryNumberNotes : '',
                          }));
                        }}
                        disabled={isLoading}
                        className='h-4 w-4 mt-1 text-yellow-600 border-gray-300 dark:border-gray-600 rounded focus:ring-yellow-500'
                      />
                      <label
                        htmlFor='requiresTemporaryNumber'
                        className='ml-2 text-sm text-gray-900 dark:text-gray-300'
                      >
                        <span className='font-medium text-yellow-600 dark:text-yellow-400'>
                          仮ナンバー必要
                        </span>
                        <span className='block text-xs text-gray-500 dark:text-gray-400 mt-0.5'>
                          仮ナンバーの申請・取得が必要な場合にチェック
                        </span>
                      </label>
                    </div>

                    {/* 仮ナンバー備考入力 */}
                    {formData.requiresTemporaryNumber && (
                      <div className='ml-6'>
                        <label
                          htmlFor='temporaryNumberNotes'
                          className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'
                        >
                          仮ナンバー関連備考
                        </label>
                        <textarea
                          id='temporaryNumberNotes'
                          name='temporaryNumberNotes'
                          value={formData.temporaryNumberNotes || ''}
                          onChange={handleInputChange}
                          rows={2}
                          placeholder='仮ナンバーの申請状況、有効期限などを記載'
                          disabled={isLoading}
                          className='w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-yellow-500 focus:border-yellow-500 disabled:opacity-50'
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* FL-205: マッチング条件 */}
            <div className='border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800'>
              <h3 className='text-sm font-semibold text-gray-900 dark:text-white mb-3'>
                マッチング条件
              </h3>
              <div className='space-y-4'>
                {/* 車検状態：あり/なし */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                    車検
                  </label>
                  <div className='flex gap-6'>
                    <div className='flex items-center'>
                      <input
                        type='radio'
                        id='has_vehicle_inspection_yes'
                        name='has_vehicle_inspection'
                        value='true'
                        checked={formData.has_vehicle_inspection === true}
                        onChange={() => {
                          setFormData(prev => ({
                            ...prev,
                            has_vehicle_inspection: true,
                          }));
                        }}
                        disabled={isLoading}
                        className='h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 focus:ring-primary-500'
                      />
                      <label
                        htmlFor='has_vehicle_inspection_yes'
                        className='ml-2 text-sm text-gray-900 dark:text-gray-300'
                      >
                        あり
                      </label>
                    </div>
                    <div className='flex items-center'>
                      <input
                        type='radio'
                        id='has_vehicle_inspection_no'
                        name='has_vehicle_inspection'
                        value='false'
                        checked={formData.has_vehicle_inspection === false}
                        onChange={() => {
                          setFormData(prev => ({
                            ...prev,
                            has_vehicle_inspection: false,
                          }));
                        }}
                        disabled={isLoading}
                        className='h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 focus:ring-primary-500'
                      />
                      <label
                        htmlFor='has_vehicle_inspection_no'
                        className='ml-2 text-sm text-gray-900 dark:text-gray-300'
                      >
                        なし
                      </label>
                    </div>
                  </div>
                </div>

                {/* 変速機：MT/AT */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                    変速機
                  </label>
                  <div className='flex gap-6'>
                    <div className='flex items-center'>
                      <input
                        type='radio'
                        id='transmission_type_manual'
                        name='transmission_type'
                        value='manual'
                        checked={formData.transmission_type === 'manual'}
                        onChange={() => {
                          setFormData(prev => ({
                            ...prev,
                            transmission_type: 'manual',
                          }));
                        }}
                        disabled={isLoading}
                        className='h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 focus:ring-primary-500'
                      />
                      <label
                        htmlFor='transmission_type_manual'
                        className='ml-2 text-sm text-gray-900 dark:text-gray-300'
                      >
                        MT
                      </label>
                    </div>
                    <div className='flex items-center'>
                      <input
                        type='radio'
                        id='transmission_type_automatic'
                        name='transmission_type'
                        value='automatic'
                        checked={formData.transmission_type === 'automatic'}
                        onChange={() => {
                          setFormData(prev => ({
                            ...prev,
                            transmission_type: 'automatic',
                          }));
                        }}
                        disabled={isLoading}
                        className='h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 focus:ring-primary-500'
                      />
                      <label
                        htmlFor='transmission_type_automatic'
                        className='ml-2 text-sm text-gray-900 dark:text-gray-300'
                      >
                        AT
                      </label>
                    </div>
                  </div>
                </div>

                {/* ハンドル位置：左/右 */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                    ハンドル位置
                  </label>
                  <div className='flex gap-6'>
                    <div className='flex items-center'>
                      <input
                        type='radio'
                        id='steering_position_right'
                        name='steering_position'
                        value='right'
                        checked={formData.steering_position === 'right'}
                        onChange={() => {
                          setFormData(prev => ({
                            ...prev,
                            steering_position: 'right',
                          }));
                        }}
                        disabled={isLoading}
                        className='h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 focus:ring-primary-500'
                      />
                      <label
                        htmlFor='steering_position_right'
                        className='ml-2 text-sm text-gray-900 dark:text-gray-300'
                      >
                        右ハンドル
                      </label>
                    </div>
                    <div className='flex items-center'>
                      <input
                        type='radio'
                        id='steering_position_left'
                        name='steering_position'
                        value='left'
                        checked={formData.steering_position === 'left'}
                        onChange={() => {
                          setFormData(prev => ({
                            ...prev,
                            steering_position: 'left',
                          }));
                        }}
                        disabled={isLoading}
                        className='h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 focus:ring-primary-500'
                      />
                      <label
                        htmlFor='steering_position_left'
                        className='ml-2 text-sm text-gray-900 dark:text-gray-300'
                      >
                        左ハンドル
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 備考 */}
            <div>
              <label
                htmlFor='remarks'
                className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
              >
                備考
              </label>
              <textarea
                name='remarks'
                id='remarks'
                value={formData.remarks}
                onChange={handleInputChange}
                rows={3}
                placeholder='特記事項があれば入力してください'
                disabled={isLoading}
                data-testid='remarks-textarea'
                className='w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 min-h-[120px] touch-target-input'
              />
            </div>

            {/* 案件ID（編集時のみ読み取り専用で表示） */}
            {isEditMode && caseId && (
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  案件ID
                </label>
                <input
                  type='text'
                  value={caseId}
                  readOnly
                  disabled
                  className='w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed min-h-[44px] font-mono text-sm'
                />
              </div>
            )}
          </div>

          {/* フォームボタン */}
          <div className='flex flex-col-reverse sm:flex-row justify-end gap-3 sm:gap-4 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-600'>
            <Button
              type='button'
              onClick={handleCancel}
              disabled={isLoading}
              variant='secondary'
              size='lg'
              className='w-full sm:w-auto'
            >
              キャンセル
            </Button>
            <Button
              type='submit'
              variant='primary'
              size='lg'
              disabled={isLoading}
              data-testid='submit-button'
              className='w-full sm:w-auto'
            >
              {isLoading ? '保存中...' : isEditMode ? '更新' : '登録'}
            </Button>
          </div>
        </form>
      </div>

      {/* ステータス管理セクション - 編集モード時のみ表示 */}
      {isEditMode && caseId && (
        <div className='mt-8'>
          <CaseStatusManager
            caseId={caseId}
            currentStatus={formData.status || null}
            onStatusUpdated={handleStatusUpdated}
            onError={handleStatusUpdateError}
          />
        </div>
      )}
    </div>
  );
};
